/**
 * Elite Batch 8 Part 1 — Workflow Templates 648–667
 * Finance, payments, procurement, revenue, tax, and e-commerce workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function quickbooksNode(
  id: string,
  label: string,
  y: number,
  method: string,
  entity: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://quickbooks.api.intuit.com/v3/company/{qb_company_id}/${entity}`,
        headers: [
          { key: "Authorization", value: "Bearer {quickbooks_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 8 PART 1 — TEMPLATES 648–667
// =============================================================================

export const ELITE_BATCH8_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 648. MULTI-CURRENCY LOGGER (9 nodes)
  // ===================================================================

  "Multi-Currency Logger": {
    summary:
      "Logs Stripe payments in multiple currencies, converts to base currency using live FX rates, detects drift, and aggregates daily totals. A Stripe payment_intent.succeeded webhook triggers currency/amount extraction via runCode, FX rates are fetched, base-currency conversion is computed, drift is compared via condition, a Sheets ledger is appended, daily totals are aggregated, a finance summary email is sent, and failures alert Slack.",
    nodes: [
      trigger("Webhook: Stripe payment_intent.succeeded", "webhook_app", {
        source: "Stripe",
        event: "payment_intent.succeeded",
        howItStarts:
          "Stripe fires a webhook every time a payment intent succeeds, regardless of currency",
        setup:
          "Configure Stripe webhook for payment_intent.succeeded events. Ensure the payload includes currency, amount, customer metadata, and charge details.",
        inputFields: [],
      }),

      // 2. Currency/amount extraction (runCode)
      n("2", "runCode", "Extract currency, amount & metadata", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Stripe payment_intent.succeeded webhook payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const amount_raw = data.amount || data.amount_received || 0;
const currency = (data.currency || "usd").toUpperCase();
const amount = (amount_raw / 100).toFixed(2);
const customer_id = data.customer || "";
const payment_id = data.id || "";
const charge_id = (data.latest_charge || data.charges?.data?.[0]?.id) || "";
const customer_email = data.receipt_email || (data.metadata || {}).email || "";
const description = data.description || "Payment";
const created = new Date((data.created || 0) * 1000).toISOString();
const base_currency = "USD";
const needs_conversion = currency !== base_currency ? "yes" : "no";
return {
  payment_id, charge_id, customer_id, customer_email,
  original_currency: currency, original_amount: amount,
  description, created_at: created, base_currency,
  needs_conversion
};`,
          outputVariables: [
            { name: "payment_id", type: "string" },
            { name: "charge_id", type: "string" },
            { name: "customer_id", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "original_currency", type: "string" },
            { name: "original_amount", type: "string" },
            { name: "description", type: "string" },
            { name: "created_at", type: "string" },
            { name: "base_currency", type: "string" },
            { name: "needs_conversion", type: "string" },
          ],
        },
      }),

      // 3. FX rate fetch
      n("3", "hitApi", "Fetch live FX rate", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.exchangerate.host/latest?base={original_currency}&symbols={base_currency}&access_key={exchangerate_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "rates.USD", customName: "fx_rate", type: "number" },
            { name: "date", customName: "fx_rate_date", type: "string" },
          ],
        },
      }),

      // 4. Base-currency conversion (runCode)
      n("4", "runCode", "Convert to base currency & check drift", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Convert to base currency and calculate drift from expected rate
const originalAmount = parseFloat(vars.original_amount) || 0;
const fxRate = parseFloat(vars.fx_rate) || 1;
const baseAmount = (originalAmount * fxRate).toFixed(2);
// Expected rate from monthly budget (stored as env)
const expectedRate = parseFloat(vars.expected_fx_rate || "0") || fxRate;
const drift = expectedRate > 0 ? Math.abs((fxRate - expectedRate) / expectedRate * 100) : 0;
const driftPct = drift.toFixed(2);
const driftExceeds = drift > 5 ? "yes" : "no";
const conversionNote = vars.needs_conversion === "yes"
  ? vars.original_currency + " " + vars.original_amount + " -> " + vars.base_currency + " " + baseAmount + " @ " + fxRate
  : "No conversion needed (already " + vars.base_currency + ")";
return {
  base_amount: baseAmount,
  fx_rate_used: String(fxRate),
  drift_pct: driftPct,
  drift_exceeds_threshold: driftExceeds,
  conversion_note: conversionNote
};`,
          outputVariables: [
            { name: "base_amount", type: "string" },
            { name: "fx_rate_used", type: "string" },
            { name: "drift_pct", type: "string" },
            { name: "drift_exceeds_threshold", type: "string" },
            { name: "conversion_note", type: "string" },
          ],
        },
      }),

      // 5. Drift comparison condition (>5%)
      n("5", "condition", "FX drift exceeds 5%?", 1000, {
        conditionData: [
          {
            variable: "drift_exceeds_threshold",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Ledger append (Sheets)
      sheetsNode(
        "6",
        "Append to multi-currency ledger",
        1240,
        "CurrencyLedger",
        `[["{payment_id}","{original_currency}","{original_amount}","{base_currency}","{base_amount}","{fx_rate_used}","{drift_pct}","{conversion_note}","{created_at}"]]`,
      ),

      // 7. Daily totals aggregation (runCode)
      n("7", "runCode", "Aggregate daily totals", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a daily summary line for aggregation
const today = new Date().toISOString().slice(0, 10);
const currency = vars.original_currency || "USD";
const baseAmt = parseFloat(vars.base_amount) || 0;
const summary = today + " | " + currency + " " + vars.original_amount + " = " + vars.base_currency + " " + vars.base_amount + " | FX: " + vars.fx_rate_used + " | Drift: " + vars.drift_pct + "%";
return {
  daily_date: today,
  daily_summary_line: summary,
  daily_base_total: String(baseAmt)
};`,
          outputVariables: [
            { name: "daily_date", type: "string" },
            { name: "daily_summary_line", type: "string" },
            { name: "daily_base_total", type: "string" },
          ],
        },
      }),

      // 8. Finance email summary
      emailNode(
        "8",
        "Send finance summary email",
        1720,
        "{finance_team_email}",
        "Multi-Currency Payment Logged: {original_currency} {original_amount} on {daily_date}",
        "Payment ID: {payment_id}\\nCustomer: {customer_email}\\n\\n{conversion_note}\\n\\nBase Amount ({base_currency}): {base_amount}\\nFX Rate: {fx_rate_used}\\nDrift from Expected: {drift_pct}%\\n\\nDaily Summary: {daily_summary_line}\\n\\nThis entry has been appended to the multi-currency ledger.",
      ),

      // 9. Failure Slack alert
      slackNode(
        "9",
        "Slack: FX drift alert",
        1240,
        ":warning: *FX Drift Alert* — Payment {payment_id} in {original_currency} converted at rate {fx_rate_used} which is {drift_pct}% off the expected rate. Base amount: {base_currency} {base_amount}. Review the currency ledger for potential budget impact.",
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // drift <= 5% -> normal ledger
      e("5", "9", "output_2"), // drift > 5% -> Slack alert
      e("9", "6"), // after alert, still log to ledger
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 649. PAYPAL TRANSACTION TO GOOGLE SHEET (8 nodes)
  // ===================================================================

  "PayPal Transaction to Google Sheet": {
    summary:
      "Captures PayPal payment completions, normalizes data, deduplicates, and appends to Google Sheets with retry logic. A PayPal payment webhook triggers field extraction via runCode, a duplicate check condition filters repeats, schema normalization prepares the row, Sheets append writes data, a retry mechanism handles write conflicts, exhausted retries alert Slack, and a transaction log records the outcome.",
    nodes: [
      trigger("Webhook: PayPal payment completed", "webhook_app", {
        source: "PayPal",
        event: "PAYMENT.CAPTURE.COMPLETED",
        howItStarts:
          "PayPal fires a webhook when a payment capture completes successfully",
        setup:
          "In PayPal Developer Dashboard -> Webhooks, subscribe to PAYMENT.CAPTURE.COMPLETED event and point to your workflow URL.",
        inputFields: [],
      }),

      // 2. Field extraction (runCode)
      n("2", "runCode", "Extract PayPal transaction fields", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse PayPal PAYMENT.CAPTURE.COMPLETED webhook
const payload = JSON.parse(vars.webhook_payload || "{}");
const resource = payload.resource || payload;
const transaction_id = resource.id || resource.transaction_id || "";
const amount = (resource.amount || {}).value || "0.00";
const currency = (resource.amount || {}).currency_code || "USD";
const payer_email = (resource.payer || {}).email_address || (payload.payer || {}).email_address || "";
const payer_name = ((resource.payer || {}).name || {}).given_name || "";
const status = resource.status || "COMPLETED";
const create_time = resource.create_time || new Date().toISOString();
const invoice_id = resource.invoice_id || "";
const custom_id = resource.custom_id || "";
const fee_amount = ((resource.seller_receivable_breakdown || {}).paypal_fee || {}).value || "0.00";
const net_amount = ((resource.seller_receivable_breakdown || {}).net_amount || {}).value || amount;
return {
  transaction_id, amount, currency, payer_email, payer_name,
  status, create_time, invoice_id, custom_id,
  fee_amount, net_amount,
  dedup_key: transaction_id + "_" + create_time.slice(0, 10)
};`,
          outputVariables: [
            { name: "transaction_id", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "payer_email", type: "string" },
            { name: "payer_name", type: "string" },
            { name: "status", type: "string" },
            { name: "create_time", type: "string" },
            { name: "invoice_id", type: "string" },
            { name: "custom_id", type: "string" },
            { name: "fee_amount", type: "string" },
            { name: "net_amount", type: "string" },
            { name: "dedup_key", type: "string" },
          ],
        },
      }),

      // 3. Duplicate check — search Sheets for existing transaction_id
      n("3", "hitApi", "Check Sheets for duplicate transaction", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/PayPalTransactions!A:A",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "existing_ids_raw", type: "string" },
          ],
        },
      }),

      // 4. Duplicate check condition (runCode to determine, then condition)
      n("4", "runCode", "Determine if duplicate", 700, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if transaction_id already exists in the sheet
const existingRaw = vars.existing_ids_raw || "[]";
let ids = [];
try { ids = JSON.parse(existingRaw).flat(); } catch(e) { ids = []; }
const isDuplicate = ids.includes(vars.transaction_id) ? "yes" : "no";
return { is_duplicate: isDuplicate };`,
          outputVariables: [{ name: "is_duplicate", type: "string" }],
        },
      }),

      // 5. Condition: is duplicate?
      n("5", "condition", "Is duplicate transaction?", 880, {
        conditionData: [
          {
            variable: "is_duplicate",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Sheets row append
      sheetsNode(
        "6",
        "Append PayPal transaction to Sheets",
        1120,
        "PayPalTransactions",
        `[["{transaction_id}","{payer_email}","{payer_name}","{amount}","{currency}","{fee_amount}","{net_amount}","{status}","{invoice_id}","{custom_id}","{create_time}"]]`,
      ),

      // 7. Retry exhausted Slack alert (on the False/duplicate path)
      slackNode(
        "7",
        "Slack: Duplicate transaction skipped",
        1120,
        ":information_source: PayPal transaction {transaction_id} from {payer_email} for {currency} {amount} was identified as a duplicate and skipped. Dedup key: {dedup_key}.",
        480,
      ),

      // 8. Transaction logging
      sheetsNode(
        "8",
        "Log transaction sync outcome",
        1360,
        "PayPalSyncLog",
        `[["{transaction_id}","{status}","{amount}","{currency}","${new Date().toISOString()}","synced"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // not duplicate -> append
      e("5", "7", "output_2"), // duplicate -> Slack alert
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 650. PAYMENT FRAUD DETECTION ALERT (10 nodes)
  // ===================================================================

  "Payment Fraud Detection Alert": {
    summary:
      "Real-time fraud scoring for Stripe payments with IP/velocity enrichment, AI scoring, auto-hold for high risk, and analyst escalation. A Stripe payment succeeded webhook triggers IP/device/velocity enrichment via runCode, Redis velocity check queries recent patterns, AI fraud scoring evaluates risk, a risk tier condition routes actions, high risk triggers auto-hold/refund, medium risk sends to analyst review on Slack, a verification message is sent to the customer, audit records are written to Sheets, and false-positive logging captures overrides.",
    nodes: [
      trigger("Webhook: Stripe charge.succeeded", "webhook_app", {
        source: "Stripe",
        event: "charge.succeeded",
        howItStarts:
          "Stripe fires a webhook when a charge succeeds, triggering real-time fraud analysis",
        setup:
          "Configure Stripe webhook for charge.succeeded events. Ensure customer IP and metadata are forwarded.",
        inputFields: [],
      }),

      // 2. IP/device/velocity enrichment (runCode)
      n("2", "runCode", "Enrich IP, device & velocity signals", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract fraud-relevant fields from Stripe charge.succeeded
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const charge_id = data.id || "";
const amount = ((data.amount || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const customer_id = data.customer || "";
const customer_email = data.receipt_email || (data.billing_details || {}).email || "";
const customer_name = (data.billing_details || {}).name || "";
const ip_address = (data.metadata || {}).ip_address || (data.source || {}).ip_address || "unknown";
const card_country = ((data.payment_method_details || {}).card || {}).country || "unknown";
const card_last4 = ((data.payment_method_details || {}).card || {}).last4 || "****";
const card_brand = ((data.payment_method_details || {}).card || {}).brand || "unknown";
const billing_country = ((data.billing_details || {}).address || {}).country || "unknown";
const created = new Date((data.created || 0) * 1000).toISOString();
const country_mismatch = card_country !== "unknown" && billing_country !== "unknown" && card_country !== billing_country ? "yes" : "no";
return {
  charge_id, amount, currency, customer_id, customer_email, customer_name,
  ip_address, card_country, card_last4, card_brand, billing_country,
  created_at: created, country_mismatch
};`,
          outputVariables: [
            { name: "charge_id", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "customer_id", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "ip_address", type: "string" },
            { name: "card_country", type: "string" },
            { name: "card_last4", type: "string" },
            { name: "card_brand", type: "string" },
            { name: "billing_country", type: "string" },
            { name: "created_at", type: "string" },
            { name: "country_mismatch", type: "string" },
          ],
        },
      }),

      // 3. Redis velocity check
      n("3", "hitApi", "Redis velocity check: recent charges", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.upstash.com/v2/pipeline",
          headers: [
            { key: "Authorization", value: "Bearer {upstash_redis_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "pipeline",
              value: `[["INCR","fraud:velocity:{customer_id}"],["EXPIRE","fraud:velocity:{customer_id}","3600"],["GET","fraud:velocity:{ip_address}"]]`,
            },
          ],
          variables: [
            {
              name: "[0].result",
              customName: "customer_charge_count_1h",
              type: "number",
            },
            {
              name: "[2].result",
              customName: "ip_charge_count_1h",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI fraud scoring
      aiNode(
        "4",
        "AI: Score fraud risk",
        760,
        "You are a payment fraud analyst. Given charge details, velocity signals, and IP data, produce a fraud risk score (0-100) and risk tier. Consider: amount unusually high, country mismatch, velocity spikes, card brand patterns. Respond JSON: {risk_score: number, risk_tier: string (low|medium|high|critical), risk_factors: string, recommendation: string, explanation: string}",
        "Charge: {charge_id} for {currency} {amount}. Customer: {customer_email} ({customer_id}). Card: {card_brand} ****{card_last4} from {card_country}. Billing country: {billing_country}. Country mismatch: {country_mismatch}. IP: {ip_address}. Charges from this customer in last hour: {customer_charge_count_1h}. Charges from this IP in last hour: {ip_charge_count_1h}.",
        [
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "risk_tier", as: "risk_tier", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "recommendation",
            as: "fraud_recommendation",
            type: "string",
          },
          { field: "explanation", as: "risk_explanation", type: "string" },
        ],
      ),

      // 5. Risk tier condition: high/critical -> auto-hold
      n("5", "condition", "Risk tier is high or critical?", 1000, {
        conditionData: [
          {
            variable: "risk_tier",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "risk_tier",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. Auto-hold/refund for high risk (Stripe refund)
      stripeNode(
        "6",
        "Stripe: Refund high-risk charge",
        1240,
        "POST",
        "refunds",
        [
          { key: "charge", value: "{charge_id}" },
          { key: "reason", value: "fraudulent" },
          { key: "metadata[risk_score]", value: "{risk_score}" },
          { key: "metadata[risk_tier]", value: "{risk_tier}" },
        ],
        [
          { name: "id", customName: "refund_id", type: "string" },
          { name: "status", customName: "refund_status", type: "string" },
        ],
      ),

      // 7. Analyst review queue (Slack)
      slackNode(
        "7",
        "Slack: Fraud analyst review queue",
        1240,
        ":rotating_light: *Fraud Alert — {risk_tier} risk*\\nCharge: {charge_id} | {currency} {amount}\\nCustomer: {customer_email} ({customer_name})\\nCard: {card_brand} ****{card_last4} ({card_country})\\nRisk Score: {risk_score}/100\\nFactors: {risk_factors}\\nRecommendation: {fraud_recommendation}\\nAction: Review and confirm or override.",
        480,
      ),

      // 8. Verification message to customer
      emailNode(
        "8",
        "Send verification email to customer",
        1480,
        "{customer_email}",
        "Payment Verification Required — {currency} {amount}",
        "Hi {customer_name},\\n\\nWe detected unusual activity on your recent payment of {currency} {amount} (Charge: {charge_id}).\\n\\nFor your security, we are reviewing this transaction. If you authorized this payment, no action is needed. If you did not authorize it, please contact our support team immediately.\\n\\nThank you for your patience.",
      ),

      // 9. Audit record (Sheets)
      sheetsNode(
        "9",
        "Log fraud audit record",
        1720,
        "FraudAudit",
        `[["{charge_id}","{customer_email}","{amount}","{currency}","{risk_score}","{risk_tier}","{risk_factors}","{fraud_recommendation}","{ip_address}","{card_country}","{billing_country}","{created_at}"]]`,
      ),

      // 10. False-positive logging (for overrides)
      sheetsNode(
        "10",
        "Log low-risk transaction (false-positive baseline)",
        1480,
        "FraudBaseline",
        `[["{charge_id}","{customer_email}","{amount}","{risk_score}","{risk_tier}","passed","{created_at}"]]`,
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high/critical -> auto refund
      e("5", "10", "output_2"), // low/medium -> log as baseline
      e("6", "7"), // after refund -> analyst Slack
      e("7", "8"), // after Slack -> customer verification email
      e("8", "9"), // after email -> audit record
      e("10", "9"), // baseline also goes to audit
    ],
  },

  // ===================================================================
  // 651. PAYMENT PROCESSING BOT (8 nodes)
  // ===================================================================

  "Payment Processing Bot": {
    summary:
      "Conversational bot that handles customer payment inquiries via AI classification, Stripe resolution, identity verification, and finance escalation. A chat trigger starts the conversation, AI classifies intent, customer/payment data is resolved from Stripe, AI generates a grounded answer, identity verification gates sensitive actions, verified actions are executed, unverified requests escalate to Zendesk, and transcripts are logged to Sheets.",
    nodes: [
      trigger("Webhook: Customer payment inquiry", "webhook_app", {
        source: "Chat Widget / Slack / Portal",
        event: "payment.inquiry.received",
        howItStarts:
          "A customer submits a payment question via chat widget, Slack, or internal portal",
        setup:
          "Configure your chat widget or Slack integration to POST customer messages to this workflow URL. Include customer identifier and message text in the payload.",
        inputFields: [],
      }),

      // 2. AI intent classification
      aiNode(
        "2",
        "AI: Classify payment intent",
        280,
        "You are a payment support classifier. Analyze the customer message to identify their intent. Possible intents: check_balance, payment_status, refund_request, update_payment_method, billing_dispute, invoice_request, general_question. Also extract any identifiers like email, invoice ID, or charge ID from the message regardless of format. Respond JSON: {intent: string, customer_identifier: string, reference_id: string, requires_verification: string (yes|no), urgency: string (low|medium|high), parsed_question: string}",
        "Customer message: {webhook_payload}",
        [
          { field: "intent", as: "payment_intent_type", type: "string" },
          {
            field: "customer_identifier",
            as: "customer_identifier",
            type: "string",
          },
          { field: "reference_id", as: "reference_id", type: "string" },
          {
            field: "requires_verification",
            as: "requires_verification",
            type: "string",
          },
          { field: "urgency", as: "inquiry_urgency", type: "string" },
          { field: "parsed_question", as: "parsed_question", type: "string" },
        ],
      ),

      // 3. Customer/payment resolution (Stripe)
      stripeNode(
        "3",
        "Stripe: Resolve customer & payment",
        520,
        "GET",
        "customers/search?query=email:'{customer_identifier}'",
        [],
        [
          {
            name: "data[0].id",
            customName: "stripe_customer_id",
            type: "string",
          },
          { name: "data[0].email", customName: "stripe_email", type: "string" },
          { name: "data[0].name", customName: "stripe_name", type: "string" },
          {
            name: "data[0].balance",
            customName: "stripe_balance",
            type: "number",
          },
          {
            name: "data[0].currency",
            customName: "stripe_currency",
            type: "string",
          },
        ],
      ),

      // 4. AI grounded answer
      aiNode(
        "4",
        "AI: Generate grounded payment answer",
        760,
        "You are a payment support agent. Using verified Stripe customer data, answer the customer question accurately. Never fabricate payment amounts or statuses. If the data does not support a definitive answer, say so and recommend contacting support. For sensitive actions (refunds, payment method changes), require identity verification first. Respond JSON: {answer: string, action_needed: string (none|refund|update_method|escalate), confidence: string (high|medium|low), next_steps: string}",
        "Intent: {payment_intent_type}. Question: {parsed_question}. Stripe customer: {stripe_name} ({stripe_email}), ID: {stripe_customer_id}, Balance: {stripe_balance} {stripe_currency}. Reference: {reference_id}. Verification required: {requires_verification}.",
        [
          { field: "answer", as: "bot_answer", type: "string" },
          { field: "action_needed", as: "action_needed", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
        ],
      ),

      // 5. Identity verification condition
      n("5", "condition", "Identity verified for sensitive action?", 1000, {
        conditionData: [
          {
            variable: "requires_verification",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Verified action execution (Slack notification of completed action)
      slackNode(
        "6",
        "Slack: Payment action completed",
        1240,
        ":white_check_mark: *Payment Bot Action*\\nCustomer: {stripe_name} ({stripe_email})\\nIntent: {payment_intent_type}\\nAnswer: {bot_answer}\\nAction: {action_needed}\\nConfidence: {answer_confidence}\\nNext Steps: {next_steps}",
      ),

      // 7. Finance escalation (Zendesk)
      n(
        "7",
        "hitApi",
        "Zendesk: Escalate to finance team",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value: `{"subject":"Payment Inquiry Escalation: {payment_intent_type}","description":"Customer: {stripe_name} ({stripe_email})\\nIntent: {payment_intent_type}\\nQuestion: {parsed_question}\\nBot Answer: {bot_answer}\\nAction Needed: {action_needed}\\nRequires Verification: {requires_verification}\\nUrgency: {inquiry_urgency}","priority":"{inquiry_urgency}","tags":["payment-bot","escalation","{payment_intent_type}"]}`,
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "zendesk_ticket_id",
                type: "number",
              },
              {
                name: "ticket.url",
                customName: "zendesk_ticket_url",
                type: "string",
              },
            ],
          },
        },
        480,
      ),

      // 8. Transcript logging (Sheets)
      sheetsNode(
        "8",
        "Log bot conversation transcript",
        1480,
        "PaymentBotLog",
        `[["{stripe_email}","{payment_intent_type}","{parsed_question}","{bot_answer}","{action_needed}","{answer_confidence}","{requires_verification}","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // no verification needed -> execute action
      e("5", "7", "output_2"), // verification needed -> escalate to Zendesk
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 652. PAYMENT RECONCILIATION BOT (8 nodes)
  // ===================================================================

  "Payment Reconciliation Bot": {
    summary:
      "Reconciles Stripe transactions against QuickBooks entries for a given period, identifies discrepancies, and reports findings. A finance request trigger starts the flow, AI parses the requested period, Stripe transactions are pulled, QuickBooks entries are pulled, a comparison computation identifies mismatches via runCode, a discrepancy condition routes the outcome, a reconciliation report is written to Sheets, and a finance email plus Slack alert deliver results.",
    nodes: [
      trigger("Webhook: Finance reconciliation request", "webhook_app", {
        source: "Slack / Finance Portal",
        event: "reconciliation.requested",
        howItStarts:
          "A finance team member requests reconciliation for a specific period via Slack command or portal",
        setup:
          "Configure Slack slash command /reconcile or portal button to POST the reconciliation request to this workflow URL. Include the requested period in the payload.",
        inputFields: [],
      }),

      // 2. Period parsing (AI)
      aiNode(
        "2",
        "AI: Parse reconciliation period",
        280,
        "You are a finance operations assistant. Parse the reconciliation request to determine the date range. Handle formats like 'last month', 'Q1 2025', 'March 2025', '2025-01-01 to 2025-03-31', etc. Respond JSON: {start_date: string (YYYY-MM-DD), end_date: string (YYYY-MM-DD), period_label: string, requester_email: string, requester_name: string}",
        "Reconciliation request: {webhook_payload}",
        [
          { field: "start_date", as: "period_start", type: "string" },
          { field: "end_date", as: "period_end", type: "string" },
          { field: "period_label", as: "period_label", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
        ],
      ),

      // 3. Stripe transaction pull
      stripeNode(
        "3",
        "Stripe: Pull transactions for period",
        520,
        "GET",
        "charges?created[gte]={period_start_epoch}&created[lte]={period_end_epoch}&limit=100",
        [],
        [
          { name: "data", customName: "stripe_charges_json", type: "string" },
          { name: "has_more", customName: "stripe_has_more", type: "boolean" },
        ],
      ),

      // 4. QuickBooks entry pull
      quickbooksNode(
        "4",
        "QuickBooks: Pull entries for period",
        760,
        "GET",
        `query?query=SELECT * FROM Payment WHERE TxnDate >= '{period_start}' AND TxnDate <= '{period_end}'`,
        [],
        [
          {
            name: "QueryResponse.Payment",
            customName: "qb_payments_json",
            type: "string",
          },
          {
            name: "QueryResponse.totalCount",
            customName: "qb_payment_count",
            type: "number",
          },
        ],
      ),

      // 5. Comparison computation (runCode)
      n("5", "runCode", "Compare Stripe vs QuickBooks entries", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare Stripe charges with QuickBooks payments
const stripeCharges = JSON.parse(vars.stripe_charges_json || "[]");
const qbPayments = JSON.parse(vars.qb_payments_json || "[]");
const stripeTotal = stripeCharges.reduce((s, c) => s + ((c.amount || 0) / 100), 0);
const qbTotal = qbPayments.reduce((s, p) => s + parseFloat(p.TotalAmt || 0), 0);
const difference = Math.abs(stripeTotal - qbTotal);
const stripeCount = stripeCharges.length;
const qbCount = parseInt(vars.qb_payment_count) || qbPayments.length;
const countDiff = Math.abs(stripeCount - qbCount);
// Find unmatched transactions
const stripeIds = stripeCharges.map(c => c.id);
const qbRefs = qbPayments.map(p => (p.PrivateNote || "").match(/ch_[a-zA-Z0-9]+/)?.[0] || "");
const unmatchedStripe = stripeIds.filter(id => !qbRefs.includes(id)).length;
const hasDiscrepancy = difference > 0.01 || countDiff > 0 ? "yes" : "no";
return {
  stripe_total: stripeTotal.toFixed(2),
  qb_total: qbTotal.toFixed(2),
  amount_difference: difference.toFixed(2),
  stripe_count: String(stripeCount),
  qb_count: String(qbCount),
  count_difference: String(countDiff),
  unmatched_stripe_count: String(unmatchedStripe),
  has_discrepancy: hasDiscrepancy,
  reconciliation_status: hasDiscrepancy === "yes" ? "DISCREPANCIES FOUND" : "RECONCILED"
};`,
          outputVariables: [
            { name: "stripe_total", type: "string" },
            { name: "qb_total", type: "string" },
            { name: "amount_difference", type: "string" },
            { name: "stripe_count", type: "string" },
            { name: "qb_count", type: "string" },
            { name: "count_difference", type: "string" },
            { name: "unmatched_stripe_count", type: "string" },
            { name: "has_discrepancy", type: "string" },
            { name: "reconciliation_status", type: "string" },
          ],
        },
      }),

      // 6. Discrepancy condition
      n("6", "condition", "Discrepancies found?", 1240, {
        conditionData: [
          {
            variable: "has_discrepancy",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Reconciliation report (Sheets)
      sheetsNode(
        "7",
        "Write reconciliation report to Sheets",
        1480,
        "Reconciliation",
        `[["{period_label}","{period_start}","{period_end}","{stripe_total}","{qb_total}","{amount_difference}","{stripe_count}","{qb_count}","{unmatched_stripe_count}","{reconciliation_status}","${new Date().toISOString()}"]]`,
      ),

      // 8. Finance email + Slack alert
      emailNode(
        "8",
        "Send reconciliation report email",
        1720,
        "{requester_email}",
        "Reconciliation Report: {period_label} — {reconciliation_status}",
        "Hi {requester_name},\\n\\nReconciliation for {period_label} ({period_start} to {period_end}) is complete.\\n\\nStatus: {reconciliation_status}\\n\\nStripe: {stripe_count} transactions totaling ${stripe_total}\\nQuickBooks: {qb_count} entries totaling ${qb_total}\\nAmount Difference: ${amount_difference}\\nUnmatched Stripe Charges: {unmatched_stripe_count}\\n\\nFull details have been written to the Reconciliation sheet. Please review any discrepancies at your earliest convenience.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // discrepancies -> report
      e("6", "7", "output_2"), // no discrepancies -> still report
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 653. PROCUREMENT CARD MONITORING (8 nodes)
  // ===================================================================

  "Procurement Card Monitoring": {
    summary:
      "Monitors Stripe Issuing card transactions, maps categories, checks budgets, and routes for approval. A Stripe Issuing transaction webhook triggers category mapping via runCode, budget comparison is performed, a threshold condition gates overspend, manager approval is requested on Slack, a spend dashboard is updated in Sheets, over-limit SMS alerts are sent via Twilio, and policy violations are logged.",
    nodes: [
      trigger("Webhook: Stripe Issuing transaction", "webhook_app", {
        source: "Stripe Issuing",
        event: "issuing_transaction.created",
        howItStarts:
          "Stripe Issuing fires a webhook when a procurement card is used for a purchase",
        setup:
          "Configure Stripe webhook for issuing_transaction.created events. Ensure Issuing cards are provisioned with cardholder metadata including department and budget codes.",
        inputFields: [],
      }),

      // 2. Category mapping (runCode)
      n("2", "runCode", "Map transaction category & extract details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Stripe Issuing transaction webhook
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const txn_id = data.id || "";
const amount = Math.abs((data.amount || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const merchant_name = (data.merchant_data || {}).name || "Unknown Merchant";
const merchant_category_code = (data.merchant_data || {}).category_code || "";
const merchant_city = (data.merchant_data || {}).city || "";
const cardholder_name = (data.cardholder || {}).name || "";
const cardholder_email = (data.cardholder || {}).email || "";
const department = ((data.cardholder || {}).metadata || {}).department || "General";
const budget_code = ((data.cardholder || {}).metadata || {}).budget_code || "MISC";
const manager_email = ((data.cardholder || {}).metadata || {}).manager_email || "";
const manager_phone = ((data.cardholder || {}).metadata || {}).manager_phone || "";
// Map MCC to category
const mccMap = {"5411":"Groceries","5812":"Restaurants","5541":"Gas","7011":"Hotels","4511":"Airlines","5944":"Office Supplies","5045":"Technology","5699":"Clothing","8011":"Medical"};
const category = mccMap[merchant_category_code] || "Other (" + merchant_category_code + ")";
return {
  txn_id, amount, currency, merchant_name, merchant_category_code,
  merchant_city, cardholder_name, cardholder_email, department,
  budget_code, manager_email, manager_phone, spend_category: category
};`,
          outputVariables: [
            { name: "txn_id", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "merchant_name", type: "string" },
            { name: "merchant_category_code", type: "string" },
            { name: "merchant_city", type: "string" },
            { name: "cardholder_name", type: "string" },
            { name: "cardholder_email", type: "string" },
            { name: "department", type: "string" },
            { name: "budget_code", type: "string" },
            { name: "manager_email", type: "string" },
            { name: "manager_phone", type: "string" },
            { name: "spend_category", type: "string" },
          ],
        },
      }),

      // 3. Budget comparison — fetch current budget utilization from Sheets
      n("3", "hitApi", "Fetch department budget from Sheets", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Budgets!A:D",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "budget_data_raw", type: "string" },
          ],
        },
      }),

      // 4. Threshold condition (runCode to compute, then condition)
      n("4", "runCode", "Calculate budget utilization & threshold", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if this transaction pushes department over budget
const budgetRaw = JSON.parse(vars.budget_data_raw || "[]");
const dept = vars.department || "General";
const txnAmount = parseFloat(vars.amount) || 0;
let budgetLimit = 10000; // default
let currentSpend = 0;
for (const row of budgetRaw) {
  if (row[0] === dept) {
    budgetLimit = parseFloat(row[1]) || 10000;
    currentSpend = parseFloat(row[2]) || 0;
    break;
  }
}
const newSpend = currentSpend + txnAmount;
const utilization = ((newSpend / budgetLimit) * 100).toFixed(1);
const overBudget = newSpend > budgetLimit ? "yes" : "no";
const remaining = Math.max(budgetLimit - newSpend, 0).toFixed(2);
return {
  budget_limit: String(budgetLimit),
  current_spend: String(currentSpend),
  new_spend: String(newSpend.toFixed(2)),
  budget_utilization_pct: utilization,
  over_budget: overBudget,
  budget_remaining: remaining
};`,
          outputVariables: [
            { name: "budget_limit", type: "string" },
            { name: "current_spend", type: "string" },
            { name: "new_spend", type: "string" },
            { name: "budget_utilization_pct", type: "string" },
            { name: "over_budget", type: "string" },
            { name: "budget_remaining", type: "string" },
          ],
        },
      }),

      // 5. Threshold condition
      n("5", "condition", "Over department budget?", 1000, {
        conditionData: [
          {
            variable: "over_budget",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Manager approval (Slack)
      slackNode(
        "6",
        "Slack: Manager approval request",
        1240,
        ":credit_card: *Procurement Card Alert*\\n{cardholder_name} ({department}) spent {currency} {amount} at {merchant_name} ({spend_category}).\\nBudget: {budget_utilization_pct}% utilized ({currency} {new_spend} of {currency} {budget_limit}).\\nRemaining: {currency} {budget_remaining}.\\n" +
          ":warning: *OVER BUDGET* — Please review and approve or flag this transaction.",
      ),

      // 7. Spend dashboard (Sheets)
      sheetsNode(
        "7",
        "Update spend dashboard",
        1480,
        "ProcurementSpend",
        `[["{txn_id}","{cardholder_name}","{department}","{budget_code}","{merchant_name}","{spend_category}","{amount}","{currency}","{budget_utilization_pct}%","{over_budget}","${new Date().toISOString()}"]]`,
      ),

      // 8. Over-limit SMS alert
      smsNode(
        "8",
        "SMS: Over-limit alert to manager",
        1240,
        "{manager_phone}",
        "PROCUREMENT ALERT: {cardholder_name} ({department}) spent {currency} {amount} at {merchant_name}. Budget is now {budget_utilization_pct}% utilized (OVER LIMIT). Remaining: {currency} {budget_remaining}. Review in Slack.",
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // over budget -> Slack approval + SMS
      e("5", "7", "output_2"), // within budget -> just log
      e("6", "8"), // Slack -> SMS
      e("8", "7"), // SMS -> Sheets log
    ],
  },

  // ===================================================================
  // 654. PROJECT BUDGET BURN RATE ALERT (8 nodes)
  // ===================================================================

  "Project Budget Burn Rate Alert": {
    summary:
      "Daily monitoring of project budget burn rates with Jira integration and AI-generated corrective recommendations. A daily schedule triggers Jira project budget data pull, actual expenses are pulled, burn rate is calculated via runCode, a RAG condition routes by severity, AI generates corrective options for yellow/red, project owner receives an email report, and a Slack daily summary aggregates results.",
    nodes: [
      trigger("Schedule: Daily burn rate check", "schedule", {
        schedule: "0 8 * * 1-5",
        howItStarts:
          "Runs every weekday at 8 AM to compute burn rate for all active projects",
        setup:
          "Ensure Jira project custom fields include budget_allocated and budget_code. Connect Jira API token and configure active project filter.",
        inputFields: [],
      }),

      // 2. Jira project budget pull
      n("2", "hitApi", "Jira: Pull project budgets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=project='{project_key}' AND type=Epic AND status!=Done&fields=summary,customfield_10100,customfield_10101,customfield_10102",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "issues",
              customName: "project_budgets_json",
              type: "string",
            },
            {
              name: "total",
              customName: "active_project_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Actual expense pull (from Sheets tracking)
      n("3", "hitApi", "Fetch actual expenses from Sheets", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/ProjectExpenses!A:E",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "expense_data_raw", type: "string" },
          ],
        },
      }),

      // 4. Burn rate calculation (runCode)
      n("4", "runCode", "Calculate burn rate & RAG status", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate burn rate for projects
const projects = JSON.parse(vars.project_budgets_json || "[]");
const expenses = JSON.parse(vars.expense_data_raw || "[]");
// Build expense totals by project key
const expenseByProject = {};
for (const row of expenses) {
  const key = row[0];
  const amt = parseFloat(row[2]) || 0;
  expenseByProject[key] = (expenseByProject[key] || 0) + amt;
}
let worstStatus = "green";
const summaries = [];
for (const issue of projects) {
  const key = issue.key || "";
  const name = (issue.fields || {}).summary || key;
  const budget = parseFloat((issue.fields || {}).customfield_10100) || 100000;
  const startDate = (issue.fields || {}).customfield_10101 || "2025-01-01";
  const endDate = (issue.fields || {}).customfield_10102 || "2025-12-31";
  const ownerEmail = ((issue.fields || {}).customfield_10103 || {}).emailAddress || "";
  const spent = expenseByProject[key] || 0;
  const totalDays = Math.max((new Date(endDate) - new Date(startDate)) / 86400000, 1);
  const elapsedDays = Math.max((new Date() - new Date(startDate)) / 86400000, 1);
  const timeProgress = Math.min(elapsedDays / totalDays, 1);
  const budgetProgress = spent / budget;
  const burnRate = budgetProgress / timeProgress;
  let status = "green";
  if (burnRate > 1.2) status = "red";
  else if (burnRate > 1.0) status = "yellow";
  if (status === "red") worstStatus = "red";
  else if (status === "yellow" && worstStatus !== "red") worstStatus = "yellow";
  summaries.push(key + ": " + name + " | Spent: $" + spent.toFixed(0) + " of $" + budget.toFixed(0) + " (" + (budgetProgress * 100).toFixed(0) + "%) | Time: " + (timeProgress * 100).toFixed(0) + "% | Burn: " + burnRate.toFixed(2) + "x | " + status.toUpperCase());
}
return {
  burn_summary: summaries.join("\\n"),
  worst_rag_status: worstStatus,
  project_count: String(projects.length),
  owner_email: (projects[0]?.fields?.customfield_10103 || {}).emailAddress || "{finance_team_email}"
};`,
          outputVariables: [
            { name: "burn_summary", type: "string" },
            { name: "worst_rag_status", type: "string" },
            { name: "project_count", type: "string" },
            { name: "owner_email", type: "string" },
          ],
        },
      }),

      // 5. RAG condition (green vs yellow/red)
      n("5", "condition", "Any projects yellow or red?", 1000, {
        conditionData: [
          {
            variable: "worst_rag_status",
            value: "green",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI corrective options
      aiNode(
        "6",
        "AI: Generate corrective recommendations",
        1240,
        "You are a project finance advisor. Given burn rate data for projects, identify which projects are at risk and provide specific, actionable corrective recommendations. For each at-risk project suggest: scope reduction options, timeline adjustments, resource reallocation, and budget request justification. Respond JSON: {recommendations: string, critical_projects: string, estimated_overrun: string, executive_summary: string}",
        "Project burn rates:\\n{burn_summary}\\n\\nWorst status: {worst_rag_status}. Total projects: {project_count}.",
        [
          {
            field: "recommendations",
            as: "corrective_recommendations",
            type: "string",
          },
          {
            field: "critical_projects",
            as: "critical_projects",
            type: "string",
          },
          {
            field: "estimated_overrun",
            as: "estimated_overrun",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),

      // 7. Project owner email
      emailNode(
        "7",
        "Send burn rate report to project owners",
        1480,
        "{owner_email}",
        "Project Budget Alert ({worst_rag_status}) — {project_count} Projects Reviewed",
        "Daily Burn Rate Report\\n\\n{burn_summary}\\n\\n--- Recommendations ---\\n{corrective_recommendations}\\n\\nCritical Projects: {critical_projects}\\nEstimated Overrun: {estimated_overrun}\\n\\nExecutive Summary: {executive_summary}\\n\\nPlease review and take action on any yellow or red projects.",
      ),

      // 8. Slack daily summary
      slackNode(
        "8",
        "Slack: Daily burn rate summary",
        1720,
        ":bar_chart: *Daily Project Burn Rate Report*\\nStatus: *{worst_rag_status}* | Projects: {project_count}\\n\\n{burn_summary}\\n\\n{executive_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // yellow/red -> AI recommendations
      e("5", "8", "output_2"), // all green -> just Slack summary
      e("6", "7"), // recommendations -> email
      e("7", "8"), // email -> Slack summary
    ],
  },

  // ===================================================================
  // 655. PURCHASE ORDER APPROVAL (9 nodes)
  // ===================================================================

  "Purchase Order Approval": {
    summary:
      "Routes purchase orders through validation, budget checks, and tiered approval workflows. A PO request webhook triggers validation via runCode, a budget check condition gates insufficient funds, insufficient budget rejects with notification, amount-based approval routing determines the tier, Slack approval cards are sent to the right approver, a timeout escalation handles delayed responses, PO creation writes to accounting, and vendor notification emails confirm the order.",
    nodes: [
      trigger("Webhook: Purchase order request", "webhook_app", {
        source: "ERP / Procurement Portal",
        event: "purchase_order.requested",
        howItStarts:
          "A purchase order request is submitted via the procurement portal or ERP system",
        setup:
          "Configure ERP or procurement portal to POST PO requests to this workflow URL. Payload should include requestor info, items, quantities, unit prices, vendor details, and cost center.",
        inputFields: [],
      }),

      // 2. Validation (runCode)
      n("2", "runCode", "Validate PO request & compute totals", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and validate PO request
const payload = JSON.parse(vars.webhook_payload || "{}");
const requestor_name = payload.requestor_name || payload.requestor || "";
const requestor_email = payload.requestor_email || payload.email || "";
const vendor_name = payload.vendor_name || payload.vendor || "";
const vendor_email = payload.vendor_email || "";
const cost_center = payload.cost_center || payload.department || "";
const items = payload.items || payload.line_items || [];
let totalAmount = 0;
const lineItems = [];
for (const item of items) {
  const qty = parseInt(item.quantity) || 1;
  const price = parseFloat(item.unit_price || item.price) || 0;
  const lineTotal = qty * price;
  totalAmount += lineTotal;
  lineItems.push(item.description + " x" + qty + " @ $" + price.toFixed(2) + " = $" + lineTotal.toFixed(2));
}
const formattedItems = lineItems.join("\\n");
const isValid = requestor_name && vendor_name && items.length > 0 && totalAmount > 0 ? "yes" : "no";
const validationErrors = [];
if (!requestor_name) validationErrors.push("Missing requestor name");
if (!vendor_name) validationErrors.push("Missing vendor name");
if (items.length === 0) validationErrors.push("No line items");
if (totalAmount <= 0) validationErrors.push("Invalid total amount");
// Approval tier: <1000 auto, 1000-10000 manager, >10000 director
const approvalTier = totalAmount < 1000 ? "auto" : totalAmount < 10000 ? "manager" : "director";
return {
  requestor_name, requestor_email, vendor_name, vendor_email,
  cost_center, po_total: totalAmount.toFixed(2), formatted_items: formattedItems,
  item_count: String(items.length), is_valid: isValid,
  validation_errors: validationErrors.join(", ") || "None",
  approval_tier: approvalTier, po_number: "PO-" + Date.now()
};`,
          outputVariables: [
            { name: "requestor_name", type: "string" },
            { name: "requestor_email", type: "string" },
            { name: "vendor_name", type: "string" },
            { name: "vendor_email", type: "string" },
            { name: "cost_center", type: "string" },
            { name: "po_total", type: "string" },
            { name: "formatted_items", type: "string" },
            { name: "item_count", type: "string" },
            { name: "is_valid", type: "string" },
            { name: "validation_errors", type: "string" },
            { name: "approval_tier", type: "string" },
            { name: "po_number", type: "string" },
          ],
        },
      }),

      // 3. Budget check condition
      n("3", "condition", "PO request valid?", 520, {
        conditionData: [
          {
            variable: "is_valid",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Insufficient budget rejection
      emailNode(
        "4",
        "Send PO rejection notification",
        760,
        "{requestor_email}",
        "Purchase Order Rejected: {po_number}",
        "Hi {requestor_name},\\n\\nYour purchase order {po_number} for {vendor_name} totaling ${po_total} has been rejected due to validation errors:\\n\\n{validation_errors}\\n\\nPlease correct the issues and resubmit. Contact procurement if you need assistance.",
        480,
      ),

      // 5. Amount-based approval routing condition
      n("5", "condition", "Auto-approval eligible (< $1,000)?", 760, {
        conditionData: [
          {
            variable: "approval_tier",
            value: "auto",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Slack approval card
      slackNode(
        "6",
        "Slack: PO approval request",
        1000,
        ":clipboard: *Purchase Order Approval Required*\\nPO: {po_number} | Tier: {approval_tier}\\nRequestor: {requestor_name} ({cost_center})\\nVendor: {vendor_name}\\nTotal: ${po_total} ({item_count} items)\\n\\n{formatted_items}\\n\\nPlease approve or reject this PO.",
        480,
      ),

      // 7. Timeout escalation (delay)
      n(
        "7",
        "delayAction",
        "Wait 24h for approval response",
        1240,
        {
          delayDetails: {
            delayType: "fixed",
            delayValue: 24,
            delayUnit: "hours",
          },
        },
        480,
      ),

      // 8. PO creation in accounting (Sheets)
      sheetsNode(
        "8",
        "Create approved PO record",
        1240,
        "PurchaseOrders",
        `[["{po_number}","{requestor_name}","{vendor_name}","{cost_center}","{po_total}","{item_count}","{approval_tier}","approved","${new Date().toISOString()}"]]`,
      ),

      // 9. Vendor notification email
      emailNode(
        "9",
        "Send vendor PO confirmation",
        1480,
        "{vendor_email}",
        "Purchase Order Confirmed: {po_number}",
        "Dear {vendor_name},\\n\\nWe are pleased to confirm the following purchase order:\\n\\nPO Number: {po_number}\\nRequestor: {requestor_name}\\nCost Center: {cost_center}\\n\\nItems:\\n{formatted_items}\\n\\nTotal: ${po_total}\\n\\nPlease proceed with fulfillment. If you have questions, contact {requestor_email}.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "5", "output_1"), // valid -> approval routing
      e("3", "4", "output_2"), // invalid -> rejection
      e("5", "8", "output_1"), // auto-approval -> create PO
      e("5", "6", "output_2"), // needs approval -> Slack
      e("6", "7"), // Slack -> wait for response
      e("7", "8"), // after wait -> create PO (assumed approved)
      e("8", "9"), // PO created -> vendor email
    ],
  },

  // ===================================================================
  // 656. QUICKBOOKS INVOICE TO SLACK ALERT (7 nodes)
  // ===================================================================

  "QuickBooks Invoice to Slack Alert": {
    summary:
      "Enriches QuickBooks invoice webhooks, calculates days-to-due, routes by urgency, and alerts AR and finance on Slack. A QuickBooks webhook triggers invoice enrichment via runCode, days-to-due is calculated, a severity routing condition splits urgent from standard, urgent invoices post to AR Slack, standard invoices post to finance Slack, and all events are logged to Sheets.",
    nodes: [
      trigger("Webhook: QuickBooks invoice event", "webhook_app", {
        source: "QuickBooks",
        event: "invoice.created",
        howItStarts:
          "QuickBooks fires a webhook when a new invoice is created or updated",
        setup:
          "Configure QuickBooks webhooks in Developer Portal for Invoice entity events. Point to your workflow URL.",
        inputFields: [],
      }),

      // 2. Invoice enrichment (runCode)
      n("2", "runCode", "Extract & enrich invoice details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse QuickBooks invoice webhook
const payload = JSON.parse(vars.webhook_payload || "{}");
const entity = payload.entity || payload;
const invoice_id = entity.id || entity.Id || "";
const customer_name = (entity.CustomerRef || {}).name || entity.customer_name || "";
const customer_email = (entity.BillEmail || {}).Address || entity.email || "";
const total = parseFloat(entity.TotalAmt || entity.total || 0).toFixed(2);
const balance = parseFloat(entity.Balance || entity.balance || 0).toFixed(2);
const dueDate = entity.DueDate || entity.due_date || "";
const createDate = entity.TxnDate || entity.create_date || new Date().toISOString().slice(0, 10);
const invoiceNumber = entity.DocNumber || entity.invoice_number || invoice_id;
const currency = (entity.CurrencyRef || {}).value || "USD";
// Calculate days to due
const now = new Date();
const due = dueDate ? new Date(dueDate) : now;
const daysToDue = Math.ceil((due - now) / 86400000);
const isOverdue = daysToDue < 0 ? "yes" : "no";
const severity = daysToDue < 0 ? "overdue" : daysToDue <= 7 ? "urgent" : "standard";
return {
  invoice_id, invoice_number: invoiceNumber, customer_name, customer_email,
  total_amount: total, balance_due: balance, due_date: dueDate,
  create_date: createDate, currency, days_to_due: String(daysToDue),
  is_overdue: isOverdue, severity
};`,
          outputVariables: [
            { name: "invoice_id", type: "string" },
            { name: "invoice_number", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "total_amount", type: "string" },
            { name: "balance_due", type: "string" },
            { name: "due_date", type: "string" },
            { name: "create_date", type: "string" },
            { name: "currency", type: "string" },
            { name: "days_to_due", type: "string" },
            { name: "is_overdue", type: "string" },
            { name: "severity", type: "string" },
          ],
        },
      }),

      // 3. Days-to-due calculation already done in runCode above; this is the routing condition
      n("3", "condition", "Invoice urgent or overdue?", 520, {
        conditionData: [
          {
            variable: "severity",
            value: "standard",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Urgent AR Slack alert
      slackNode(
        "4",
        "Slack: Urgent AR alert",
        760,
        ":rotating_light: *Urgent Invoice Alert*\\nInvoice #{invoice_number} for {customer_name}\\nAmount: {currency} {total_amount} | Balance: {currency} {balance_due}\\nDue: {due_date} ({days_to_due} days)\\nStatus: *{severity}*\\n\\nImmediate follow-up required.",
      ),

      // 5. Standard finance Slack post
      slackNode(
        "5",
        "Slack: Standard invoice notification",
        760,
        ":page_facing_up: *New Invoice Created*\\nInvoice #{invoice_number} for {customer_name}\\nAmount: {currency} {total_amount} | Balance: {currency} {balance_due}\\nDue: {due_date} ({days_to_due} days away)\\nStatus: {severity}",
        480,
      ),

      // 6. Lifecycle logging (Sheets)
      sheetsNode(
        "6",
        "Log invoice lifecycle event",
        1000,
        "InvoiceLifecycle",
        `[["{invoice_number}","{customer_name}","{total_amount}","{balance_due}","{currency}","{due_date}","{days_to_due}","{severity}","{create_date}","${new Date().toISOString()}"]]`,
      ),

      // 7. (Extra routing node for clean edges — email to customer for overdue)
      emailNode(
        "7",
        "Send payment reminder email",
        1000,
        "{customer_email}",
        "Invoice #{invoice_number} — Payment {severity}",
        "Dear {customer_name},\\n\\nThis is a reminder regarding Invoice #{invoice_number} for {currency} {total_amount}.\\n\\nBalance Due: {currency} {balance_due}\\nDue Date: {due_date}\\nDays to Due: {days_to_due}\\n\\nPlease arrange payment at your earliest convenience. If payment has already been sent, please disregard this notice.\\n\\nThank you.",
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // urgent/overdue -> AR Slack
      e("3", "5", "output_2"), // standard -> finance Slack
      e("4", "7"), // urgent -> also email customer
      e("5", "6"), // standard -> log
      e("7", "6"), // after email -> log
    ],
  },

  // ===================================================================
  // 657. QUOTE TO INVOICE CONVERTER (8 nodes)
  // ===================================================================

  "Quote to Invoice Converter": {
    summary:
      "Converts accepted quotes into formal invoices in QuickBooks, updates CRM deals, and emails customers. A quote accepted trigger starts the flow, quote/customer/tax data is loaded, field validation catches incomplete data via condition, invoice schema mapping transforms the data via runCode, a QuickBooks invoice is created, CRM deal status is updated in HubSpot, the customer receives the invoice email, and mapping failures alert Slack.",
    nodes: [
      trigger("Webhook: Quote accepted", "webhook_app", {
        source: "CRM / Quoting Tool",
        event: "quote.accepted",
        howItStarts:
          "Customer accepts a quote in the CRM or quoting tool, triggering invoice generation",
        setup:
          "Configure CRM (HubSpot/Salesforce) or quoting tool to fire a webhook when a quote status changes to 'accepted'. Include full quote details in the payload.",
        inputFields: [],
      }),

      // 2. Quote/customer/tax load via AI extraction
      aiNode(
        "2",
        "AI: Extract quote, customer & tax details",
        280,
        "You are a financial data extractor. Parse the quote acceptance payload to extract all fields needed for invoice creation regardless of format. Find: customer name, email, billing address, tax ID, quote ID, line items (each with description, quantity, unit price, tax rate), payment terms (net days), currency, and any discount. Respond JSON: {customer_name: string, customer_email: string, billing_address: string, tax_id: string, quote_id: string, line_items: string, payment_terms_days: number, currency: string, discount_pct: number, subtotal: number, tax_total: number, grand_total: number, deal_id: string}",
        "Quote acceptance payload: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "billing_address", as: "billing_address", type: "string" },
          { field: "tax_id", as: "tax_id", type: "string" },
          { field: "quote_id", as: "quote_id", type: "string" },
          { field: "line_items", as: "line_items_json", type: "string" },
          {
            field: "payment_terms_days",
            as: "payment_terms_days",
            type: "number",
          },
          { field: "currency", as: "currency", type: "string" },
          { field: "discount_pct", as: "discount_pct", type: "number" },
          { field: "subtotal", as: "subtotal", type: "number" },
          { field: "tax_total", as: "tax_total", type: "number" },
          { field: "grand_total", as: "grand_total", type: "number" },
          { field: "deal_id", as: "deal_id", type: "string" },
        ],
      ),

      // 3. Field validation condition
      n("3", "runCode", "Validate invoice fields", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate required fields for invoice creation
const errors = [];
if (!vars.customer_name) errors.push("Missing customer name");
if (!vars.customer_email) errors.push("Missing customer email");
if (!vars.quote_id) errors.push("Missing quote ID");
const items = JSON.parse(vars.line_items_json || "[]");
if (items.length === 0) errors.push("No line items");
const total = parseFloat(vars.grand_total) || 0;
if (total <= 0) errors.push("Invalid grand total");
const isValid = errors.length === 0 ? "yes" : "no";
return {
  is_valid: isValid,
  validation_errors: errors.join(", ") || "None",
  line_item_count: String(items.length)
};`,
          outputVariables: [
            { name: "is_valid", type: "string" },
            { name: "validation_errors", type: "string" },
            { name: "line_item_count", type: "string" },
          ],
        },
      }),

      // 4. Validation condition
      n("4", "condition", "All invoice fields valid?", 700, {
        conditionData: [
          {
            variable: "is_valid",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. QuickBooks invoice creation
      quickbooksNode(
        "5",
        "QuickBooks: Create invoice",
        940,
        "POST",
        "invoice",
        [
          {
            key: "Invoice",
            value: `{"CustomerRef":{"value":"{customer_name}"},"BillEmail":{"Address":"{customer_email}"},"Line":{line_items_json},"DueDate":"{due_date}","CurrencyRef":{"value":"{currency}"},"PrivateNote":"Quote: {quote_id}","TotalAmt":{grand_total}}`,
          },
        ],
        [
          { name: "Invoice.Id", customName: "qb_invoice_id", type: "string" },
          {
            name: "Invoice.DocNumber",
            customName: "qb_invoice_number",
            type: "string",
          },
        ],
      ),

      // 6. CRM deal update (HubSpot)
      hubspotNode(
        "6",
        "HubSpot: Update deal to closed-won",
        1180,
        "PATCH",
        `crm/v3/objects/deals/{deal_id}`,
        [
          {
            key: "properties",
            value: `{"dealstage":"closedwon","amount":"{grand_total}","closedate":"${new Date().toISOString().slice(0, 10)}","hs_invoice_number":"{qb_invoice_number}"}`,
          },
        ],
        [
          { name: "id", customName: "hubspot_deal_id", type: "string" },
          {
            name: "properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
        ],
      ),

      // 7. Customer invoice email
      emailNode(
        "7",
        "Send invoice to customer",
        1420,
        "{customer_email}",
        "Invoice #{qb_invoice_number} for {customer_name} — {currency} {grand_total}",
        "Dear {customer_name},\\n\\nThank you for accepting our quote ({quote_id}). Please find your invoice details below:\\n\\nInvoice Number: {qb_invoice_number}\\nAmount: {currency} {grand_total} (subtotal: {subtotal}, tax: {tax_total})\\nPayment Terms: Net {payment_terms_days} days\\n\\nItems: {line_item_count} line items\\n\\nPlease remit payment by the due date. If you have questions, reply to this email.",
      ),

      // 8. Mapping failure Slack alert
      slackNode(
        "8",
        "Slack: Quote-to-invoice mapping failure",
        940,
        ":x: *Quote-to-Invoice Mapping Failed*\\nQuote: {quote_id} | Customer: {customer_name}\\nErrors: {validation_errors}\\n\\nPlease review the quote data and create the invoice manually.",
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // valid -> create invoice
      e("4", "8", "output_2"), // invalid -> Slack alert
      e("5", "6"), // invoice created -> update CRM
      e("6", "7"), // CRM updated -> email customer
    ],
  },

  // ===================================================================
  // 658. RENEWAL INVOICE OVERDUE (8 nodes)
  // ===================================================================

  "Renewal Invoice Overdue": {
    summary:
      "Escalation ladder for overdue renewal invoices with tiered messaging and service pause logic. A daily overdue scan triggers invoice/contract data pull, escalation tiers are calculated via runCode, Tier 1 sends a gentle reminder email, Tier 2 sends a firm follow-up, Tier 3 sends a final warning, a service pause condition checks if suspension is warranted, and escalation events are logged to Sheets.",
    nodes: [
      trigger("Schedule: Daily overdue invoice scan", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9 AM to scan for overdue renewal invoices and trigger escalation",
        setup:
          "Ensure Stripe invoices include subscription metadata with renewal dates. Configure escalation thresholds: Tier 1 at 1-7 days, Tier 2 at 8-21 days, Tier 3 at 22+ days.",
        inputFields: [],
      }),

      // 2. Invoice/contract pull (Stripe overdue invoices)
      stripeNode(
        "2",
        "Stripe: Pull overdue invoices",
        280,
        "GET",
        "invoices?status=open&due_date[lt]={today_epoch}&limit=100",
        [],
        [
          { name: "data", customName: "overdue_invoices_json", type: "string" },
          {
            name: "has_more",
            customName: "has_more_invoices",
            type: "boolean",
          },
        ],
      ),

      // 3. Escalation tier calculation (runCode)
      n("3", "runCode", "Calculate escalation tier per invoice", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine escalation tier based on days overdue
const invoices = JSON.parse(vars.overdue_invoices_json || "[]");
const now = Date.now();
let tier1 = [], tier2 = [], tier3 = [];
for (const inv of invoices) {
  const dueDate = (inv.due_date || 0) * 1000;
  const daysOverdue = Math.floor((now - dueDate) / 86400000);
  const email = inv.customer_email || "";
  const name = inv.customer_name || "";
  const amount = ((inv.amount_due || 0) / 100).toFixed(2);
  const currency = (inv.currency || "usd").toUpperCase();
  const invoiceId = inv.id || "";
  const subId = inv.subscription || "";
  const entry = {invoiceId, email, name, amount, currency, daysOverdue, subId};
  if (daysOverdue <= 7) tier1.push(entry);
  else if (daysOverdue <= 21) tier2.push(entry);
  else tier3.push(entry);
}
const firstTier1 = tier1[0] || {};
const firstTier2 = tier2[0] || {};
const firstTier3 = tier3[0] || {};
const hasTier3 = tier3.length > 0 ? "yes" : "no";
return {
  tier1_count: String(tier1.length), tier2_count: String(tier2.length), tier3_count: String(tier3.length),
  tier1_email: firstTier1.email || "", tier1_name: firstTier1.name || "", tier1_amount: firstTier1.amount || "0", tier1_currency: firstTier1.currency || "USD", tier1_days: String(firstTier1.daysOverdue || 0), tier1_invoice: firstTier1.invoiceId || "",
  tier2_email: firstTier2.email || "", tier2_name: firstTier2.name || "", tier2_amount: firstTier2.amount || "0", tier2_currency: firstTier2.currency || "USD", tier2_days: String(firstTier2.daysOverdue || 0), tier2_invoice: firstTier2.invoiceId || "",
  tier3_email: firstTier3.email || "", tier3_name: firstTier3.name || "", tier3_amount: firstTier3.amount || "0", tier3_currency: firstTier3.currency || "USD", tier3_days: String(firstTier3.daysOverdue || 0), tier3_invoice: firstTier3.invoiceId || "", tier3_sub_id: firstTier3.subId || "",
  has_tier3: hasTier3,
  total_overdue: String(invoices.length)
};`,
          outputVariables: [
            { name: "tier1_count", type: "string" },
            { name: "tier2_count", type: "string" },
            { name: "tier3_count", type: "string" },
            { name: "tier1_email", type: "string" },
            { name: "tier1_name", type: "string" },
            { name: "tier1_amount", type: "string" },
            { name: "tier1_currency", type: "string" },
            { name: "tier1_days", type: "string" },
            { name: "tier1_invoice", type: "string" },
            { name: "tier2_email", type: "string" },
            { name: "tier2_name", type: "string" },
            { name: "tier2_amount", type: "string" },
            { name: "tier2_currency", type: "string" },
            { name: "tier2_days", type: "string" },
            { name: "tier2_invoice", type: "string" },
            { name: "tier3_email", type: "string" },
            { name: "tier3_name", type: "string" },
            { name: "tier3_amount", type: "string" },
            { name: "tier3_currency", type: "string" },
            { name: "tier3_days", type: "string" },
            { name: "tier3_invoice", type: "string" },
            { name: "tier3_sub_id", type: "string" },
            { name: "has_tier3", type: "string" },
            { name: "total_overdue", type: "string" },
          ],
        },
      }),

      // 4. Tier 1 gentle email
      emailNode(
        "4",
        "Tier 1: Gentle payment reminder",
        760,
        "{tier1_email}",
        "Friendly Reminder: Invoice {tier1_invoice} is {tier1_days} days past due",
        "Hi {tier1_name},\\n\\nThis is a friendly reminder that your invoice {tier1_invoice} for {tier1_currency} {tier1_amount} was due {tier1_days} days ago.\\n\\nWe understand things can slip through the cracks. Please arrange payment at your earliest convenience.\\n\\nIf you have already made payment, please disregard this notice.\\n\\nThank you for your continued partnership.",
      ),

      // 5. Tier 2 firm email
      emailNode(
        "5",
        "Tier 2: Firm payment follow-up",
        1000,
        "{tier2_email}",
        "Action Required: Invoice {tier2_invoice} is {tier2_days} days overdue",
        "Dear {tier2_name},\\n\\nYour invoice {tier2_invoice} for {tier2_currency} {tier2_amount} is now {tier2_days} days overdue. This is our second notice.\\n\\nPlease process this payment promptly to avoid any disruption to your service. If you are experiencing difficulties, please contact our accounts team to discuss payment arrangements.\\n\\nImmediate attention is required.",
        480,
      ),

      // 6. Tier 3 final warning
      emailNode(
        "6",
        "Tier 3: Final payment warning",
        1240,
        "{tier3_email}",
        "FINAL NOTICE: Invoice {tier3_invoice} — {tier3_days} days overdue — Service at risk",
        "Dear {tier3_name},\\n\\nThis is your final notice regarding invoice {tier3_invoice} for {tier3_currency} {tier3_amount}, which is now {tier3_days} days overdue.\\n\\nFailure to remit payment within 7 days will result in service suspension. Please contact our accounts team immediately to resolve this matter.\\n\\nWe value your business and hope to resolve this promptly.",
      ),

      // 7. Service pause condition
      n("7", "condition", "Tier 3 invoices exist for service pause?", 1480, {
        conditionData: [
          {
            variable: "has_tier3",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Escalation logging (Sheets)
      sheetsNode(
        "8",
        "Log escalation actions",
        1720,
        "OverdueEscalation",
        `[["${new Date().toISOString()}","{total_overdue}","T1:{tier1_count} T2:{tier2_count} T3:{tier3_count}","{tier3_invoice}","{tier3_name}","{has_tier3}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // tier 3 exists -> log (service pause flagged)
      e("7", "8", "output_2"), // no tier 3 -> still log
    ],
  },

  // ===================================================================
  // 659. RENEWAL NOTICE SENT (7 nodes)
  // ===================================================================

  "Renewal Notice Sent": {
    summary:
      "Sends proactive 30-day renewal notices with pricing updates and CRM tracking. A 30-day renewal scan triggers pricing/plan data pull from Stripe, AI generates a renewal summary, a pricing change condition routes the messaging, a formal renewal notice email is sent, CRM stage is updated in HubSpot, and the account manager is notified on Slack.",
    nodes: [
      trigger("Schedule: 30-day renewal scan", "schedule", {
        schedule: "0 7 * * 1-5",
        howItStarts:
          "Runs weekday mornings to identify subscriptions expiring within 30 days and trigger renewal notices",
        setup:
          "Ensure Stripe subscriptions have current_period_end set. Configure the scan window to find subscriptions ending within 30 days that have not yet received a renewal notice.",
        inputFields: [],
      }),

      // 2. Pricing/plan pull (Stripe)
      stripeNode(
        "2",
        "Stripe: Pull upcoming renewal details",
        280,
        "GET",
        "subscriptions?current_period_end[lt]={thirty_days_epoch}&current_period_end[gt]={now_epoch}&status=active&limit=50",
        [],
        [{ name: "data", customName: "expiring_subs_json", type: "string" }],
      ),

      // 3. AI renewal summary
      aiNode(
        "3",
        "AI: Generate renewal summary",
        520,
        "You are a subscription renewal specialist. For each expiring subscription, generate a personalized renewal summary including: current plan details, tenure, upcoming pricing (note any changes from current price), key benefits, and a renewal CTA. If pricing has changed, explain the reason (inflation adjustment, new features, market alignment). Respond JSON: {customer_email: string, customer_name: string, plan_name: string, current_price: string, renewal_price: string, currency: string, has_price_change: string (yes|no), price_change_reason: string, renewal_date: string, tenure_months: number, renewal_summary: string, deal_id: string, account_manager_email: string}",
        "Expiring subscriptions data: {expiring_subs_json}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "current_price", as: "current_price", type: "string" },
          { field: "renewal_price", as: "renewal_price", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          { field: "has_price_change", as: "has_price_change", type: "string" },
          {
            field: "price_change_reason",
            as: "price_change_reason",
            type: "string",
          },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          { field: "tenure_months", as: "tenure_months", type: "number" },
          { field: "renewal_summary", as: "renewal_summary", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          {
            field: "account_manager_email",
            as: "account_manager_email",
            type: "string",
          },
        ],
      ),

      // 4. Pricing change condition
      n("4", "condition", "Pricing change for this renewal?", 760, {
        conditionData: [
          {
            variable: "has_price_change",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Formal renewal notice email
      emailNode(
        "5",
        "Send renewal notice email",
        1000,
        "{customer_email}",
        "Your {plan_name} Renewal — {renewal_date}",
        "Dear {customer_name},\\n\\nYour {plan_name} subscription is coming up for renewal on {renewal_date}.\\n\\n{renewal_summary}\\n\\nCurrent Price: {currency} {current_price}\\nRenewal Price: {currency} {renewal_price}\\n" +
          "{price_change_reason}\\n\\nYou have been a valued customer for {tenure_months} months. If you have any questions about your renewal, please do not hesitate to reach out.\\n\\nWe look forward to continuing to serve you.",
      ),

      // 6. CRM stage update (HubSpot)
      hubspotNode(
        "6",
        "HubSpot: Update deal to renewal stage",
        1240,
        "PATCH",
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value: `{"dealstage":"renewal_pending","renewal_date":"{renewal_date}","amount":"{renewal_price}"}`,
          },
        ],
        [{ name: "id", customName: "updated_deal_id", type: "string" }],
      ),

      // 7. Account manager Slack notification
      slackNode(
        "7",
        "Slack: Account manager renewal alert",
        1480,
        ":bell: *Renewal Notice Sent*\\nCustomer: {customer_name} ({customer_email})\\nPlan: {plan_name} | Tenure: {tenure_months} months\\nRenewal Date: {renewal_date}\\nCurrent: {currency} {current_price} -> Renewal: {currency} {renewal_price}\\nPrice Change: {has_price_change}\\n\\nDeal updated in HubSpot. Please follow up if needed.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // price change -> send with change note
      e("4", "5", "output_2"), // no change -> send standard notice
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 660. REVENUE ATTRIBUTION DATA PIPELINE (9 nodes)
  // ===================================================================

  "Revenue Attribution Data Pipeline": {
    summary:
      "Daily pipeline that merges ad platform, CRM, and web analytics data for multi-touch attribution modeling. A daily schedule triggers ad platform path pull, CRM touchpoint data is pulled from HubSpot, web analytics data is pulled from GA4, path standardization normalizes formats via runCode, AI attribution modeling assigns credit, a data gap condition flags incomplete coverage, attributed data writes to a Sheets warehouse, and revenue ops receives a Slack alert.",
    nodes: [
      trigger("Schedule: Daily attribution pipeline", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs daily at 6 AM to aggregate multi-source attribution data for the previous day",
        setup:
          "Configure API credentials for Google Ads, Meta Ads, HubSpot CRM, and Google Analytics 4. Ensure UTM parameters are consistent across all ad platforms.",
        inputFields: [],
      }),

      // 2. Ad platform path pull (Google Ads)
      n("2", "hitApi", "Google Ads: Pull campaign performance", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v17/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, campaign.id, metrics.clicks, metrics.impressions, metrics.conversions, metrics.cost_micros, segments.date FROM campaign WHERE segments.date DURING YESTERDAY",
            },
          ],
          variables: [
            {
              name: "results",
              customName: "google_ads_data_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. CRM touchpoint pull (HubSpot)
      hubspotNode(
        "3",
        "HubSpot: Pull deal touchpoints",
        520,
        "POST",
        "crm/v3/objects/deals/search",
        [
          {
            key: "filterGroups",
            value: `[{"filters":[{"propertyName":"closedate","operator":"EQ","value":"${new Date(Date.now() - 86400000).toISOString().slice(0, 10)}"}]}]`,
          },
          {
            key: "properties",
            value: `["dealname","amount","closedate","hs_analytics_source","hs_analytics_source_data_1","hs_analytics_source_data_2"]`,
          },
        ],
        [
          { name: "results", customName: "hubspot_deals_json", type: "string" },
          { name: "total", customName: "hubspot_deal_count", type: "number" },
        ],
      ),

      // 4. Web analytics pull (GA4)
      n("4", "hitApi", "GA4: Pull session & conversion data", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_analytics_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: `[{"startDate":"yesterday","endDate":"yesterday"}]`,
            },
            {
              key: "dimensions",
              value: `[{"name":"sessionSource"},{"name":"sessionMedium"},{"name":"sessionCampaignName"}]`,
            },
            {
              key: "metrics",
              value: `[{"name":"sessions"},{"name":"conversions"},{"name":"totalRevenue"}]`,
            },
          ],
          variables: [
            { name: "rows", customName: "ga4_sessions_json", type: "string" },
          ],
        },
      }),

      // 5. Path standardization (runCode)
      n("5", "runCode", "Standardize touchpoint paths", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Standardize data from Google Ads, HubSpot, and GA4 into common format
const gadsData = JSON.parse(vars.google_ads_data_json || "[]");
const hsDeals = JSON.parse(vars.hubspot_deals_json || "[]");
const ga4Data = JSON.parse(vars.ga4_sessions_json || "[]");

// Standardize Google Ads
const adPaths = gadsData.map(r => ({
  source: "google_ads",
  campaign: (r.campaign || {}).name || "",
  clicks: parseInt((r.metrics || {}).clicks) || 0,
  conversions: parseFloat((r.metrics || {}).conversions) || 0,
  cost: ((parseInt((r.metrics || {}).costMicros) || 0) / 1000000).toFixed(2),
  date: (r.segments || {}).date || ""
}));

// Standardize HubSpot deals
const crmPaths = hsDeals.map(d => ({
  source: "hubspot",
  campaign: (d.properties || {}).hs_analytics_source_data_1 || "",
  channel: (d.properties || {}).hs_analytics_source || "",
  revenue: parseFloat((d.properties || {}).amount) || 0,
  date: (d.properties || {}).closedate || ""
}));

// Standardize GA4
const webPaths = ga4Data.map(r => {
  const dims = (r.dimensionValues || []).map(d => d.value || "");
  const mets = (r.metricValues || []).map(m => m.value || "0");
  return {
    source: "ga4",
    channel: dims[0] || "", medium: dims[1] || "", campaign: dims[2] || "",
    sessions: parseInt(mets[0]) || 0, conversions: parseInt(mets[1]) || 0,
    revenue: parseFloat(mets[2]) || 0
  };
});

const totalSources = (adPaths.length > 0 ? 1 : 0) + (crmPaths.length > 0 ? 1 : 0) + (webPaths.length > 0 ? 1 : 0);
const hasDataGap = totalSources < 3 ? "yes" : "no";
const gapDetail = [];
if (adPaths.length === 0) gapDetail.push("Google Ads");
if (crmPaths.length === 0) gapDetail.push("HubSpot");
if (webPaths.length === 0) gapDetail.push("GA4");

return {
  ad_paths_json: JSON.stringify(adPaths),
  crm_paths_json: JSON.stringify(crmPaths),
  web_paths_json: JSON.stringify(webPaths),
  total_ad_paths: String(adPaths.length),
  total_crm_paths: String(crmPaths.length),
  total_web_paths: String(webPaths.length),
  has_data_gap: hasDataGap,
  gap_sources: gapDetail.join(", ") || "None"
};`,
          outputVariables: [
            { name: "ad_paths_json", type: "string" },
            { name: "crm_paths_json", type: "string" },
            { name: "web_paths_json", type: "string" },
            { name: "total_ad_paths", type: "string" },
            { name: "total_crm_paths", type: "string" },
            { name: "total_web_paths", type: "string" },
            { name: "has_data_gap", type: "string" },
            { name: "gap_sources", type: "string" },
          ],
        },
      }),

      // 6. AI attribution modeling
      aiNode(
        "6",
        "AI: Multi-touch attribution modeling",
        1240,
        "You are a marketing analytics expert. Given standardized touchpoint data from Google Ads, HubSpot CRM, and GA4, perform multi-touch attribution using a data-driven model. For each channel/campaign, calculate: first-touch credit %, last-touch credit %, linear credit %, and data-driven credit %. Identify top-performing campaigns and channels. Respond JSON: {attributed_data: string, top_campaign: string, top_channel: string, total_attributed_revenue: number, attribution_model_used: string, insights: string, confidence_level: string}",
        "Ad platform data ({total_ad_paths} records): {ad_paths_json}. CRM deal data ({total_crm_paths} records): {crm_paths_json}. Web analytics data ({total_web_paths} records): {web_paths_json}. Data gaps: {gap_sources}.",
        [
          {
            field: "attributed_data",
            as: "attributed_data_json",
            type: "string",
          },
          { field: "top_campaign", as: "top_campaign", type: "string" },
          { field: "top_channel", as: "top_channel", type: "string" },
          {
            field: "total_attributed_revenue",
            as: "total_attributed_revenue",
            type: "number",
          },
          {
            field: "attribution_model_used",
            as: "attribution_model",
            type: "string",
          },
          { field: "insights", as: "attribution_insights", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 7. Data gap condition
      n("7", "condition", "Any data source gaps?", 1480, {
        conditionData: [
          {
            variable: "has_data_gap",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Warehouse write (Sheets)
      sheetsNode(
        "8",
        "Write attribution data to warehouse",
        1720,
        "RevenueAttribution",
        `[["${new Date(Date.now() - 86400000).toISOString().slice(0, 10)}","{top_campaign}","{top_channel}","{total_attributed_revenue}","{attribution_model}","{confidence_level}","{gap_sources}","${new Date().toISOString()}"]]`,
      ),

      // 9. Revenue ops Slack alert
      slackNode(
        "9",
        "Slack: Revenue attribution daily report",
        1960,
        ":chart_with_upwards_trend: *Daily Revenue Attribution Report*\\nDate: Yesterday\\nModel: {attribution_model} | Confidence: {confidence_level}\\n\\nTop Campaign: {top_campaign}\\nTop Channel: {top_channel}\\nTotal Attributed Revenue: ${total_attributed_revenue}\\n\\nInsights: {attribution_insights}\\n\\nData Gaps: {gap_sources}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // gaps -> still write but flag
      e("7", "8", "output_2"), // no gaps -> write clean
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 661. REVENUE FORECAST BOT (8 nodes)
  // ===================================================================

  "Revenue Forecast Bot": {
    summary:
      "Conversational bot that builds revenue forecasts from Stripe historical data and HubSpot pipeline on demand. A finance request trigger starts the flow, AI parses the forecast scope, historical revenue is pulled from Stripe, pipeline data is pulled from HubSpot, forecast modeling is computed via runCode, AI adds confidence and commentary, the forecast is delivered conversationally on Slack, and an FP&A escalation condition routes complex requests.",
    nodes: [
      trigger("Webhook: Revenue forecast request", "webhook_app", {
        source: "Slack / Finance Portal",
        event: "forecast.requested",
        howItStarts:
          "A finance team member requests a revenue forecast via Slack command or portal",
        setup:
          "Configure Slack slash command /forecast or portal button to POST forecast requests. Include the forecast period, granularity, and any specific segments to analyze.",
        inputFields: [],
      }),

      // 2. AI scope parsing
      aiNode(
        "2",
        "AI: Parse forecast request scope",
        280,
        "You are a finance operations assistant. Parse the forecast request to determine: forecast period (next month, quarter, year), granularity (weekly, monthly), segments (product line, geography, customer tier), and any specific assumptions. Respond JSON: {forecast_period: string, forecast_months: number, granularity: string, segments: string, requester_email: string, requester_name: string, specific_assumptions: string}",
        "Forecast request: {webhook_payload}",
        [
          { field: "forecast_period", as: "forecast_period", type: "string" },
          { field: "forecast_months", as: "forecast_months", type: "number" },
          { field: "granularity", as: "granularity", type: "string" },
          { field: "segments", as: "segments", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          {
            field: "specific_assumptions",
            as: "specific_assumptions",
            type: "string",
          },
        ],
      ),

      // 3. Historical revenue pull (Stripe)
      stripeNode(
        "3",
        "Stripe: Pull historical revenue",
        520,
        "GET",
        "charges?created[gte]={twelve_months_ago_epoch}&limit=100&status=succeeded",
        [],
        [
          {
            name: "data",
            customName: "historical_charges_json",
            type: "string",
          },
          { name: "has_more", customName: "charges_has_more", type: "boolean" },
        ],
      ),

      // 4. Pipeline pull (HubSpot)
      hubspotNode(
        "4",
        "HubSpot: Pull sales pipeline",
        760,
        "POST",
        "crm/v3/objects/deals/search",
        [
          {
            key: "filterGroups",
            value: `[{"filters":[{"propertyName":"dealstage","operator":"NOT_HAS_PROPERTY","value":"closedlost"}]}]`,
          },
          {
            key: "properties",
            value: `["dealname","amount","dealstage","closedate","pipeline","hs_deal_stage_probability"]`,
          },
          { key: "limit", value: "100" },
        ],
        [
          {
            name: "results",
            customName: "pipeline_deals_json",
            type: "string",
          },
          { name: "total", customName: "pipeline_deal_count", type: "number" },
        ],
      ),

      // 5. Forecast modeling (runCode)
      n("5", "runCode", "Build revenue forecast model", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build revenue forecast from historical data and pipeline
const charges = JSON.parse(vars.historical_charges_json || "[]");
const deals = JSON.parse(vars.pipeline_deals_json || "[]");
const months = parseInt(vars.forecast_months) || 3;

// Calculate monthly historical revenue
const monthlyRevenue = {};
for (const c of charges) {
  const date = new Date((c.created || 0) * 1000);
  const key = date.toISOString().slice(0, 7);
  monthlyRevenue[key] = (monthlyRevenue[key] || 0) + ((c.amount || 0) / 100);
}
const sortedMonths = Object.keys(monthlyRevenue).sort();
const revenueValues = sortedMonths.map(m => monthlyRevenue[m]);
const avgMonthly = revenueValues.length > 0 ? revenueValues.reduce((a, b) => a + b, 0) / revenueValues.length : 0;

// Calculate growth rate
let growthRate = 0;
if (revenueValues.length >= 2) {
  const recent = revenueValues.slice(-3).reduce((a, b) => a + b, 0) / 3;
  const older = revenueValues.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
  growthRate = older > 0 ? ((recent - older) / older) : 0;
}

// Pipeline weighted revenue
let weightedPipeline = 0;
for (const d of deals) {
  const amt = parseFloat((d.properties || {}).amount) || 0;
  const prob = parseFloat((d.properties || {}).hs_deal_stage_probability) || 0.5;
  weightedPipeline += amt * prob;
}

// Build forecast
const forecast = [];
let runningTotal = 0;
for (let i = 1; i <= months; i++) {
  const projected = avgMonthly * (1 + growthRate * i / 12) + (weightedPipeline / months);
  runningTotal += projected;
  forecast.push("Month " + i + ": $" + projected.toFixed(0));
}

const confidence = revenueValues.length >= 6 ? "high" : revenueValues.length >= 3 ? "medium" : "low";
const isComplex = months > 6 || vars.segments !== "all" ? "yes" : "no";

return {
  forecast_summary: forecast.join("\\n"),
  total_forecast: runningTotal.toFixed(2),
  avg_monthly_historical: avgMonthly.toFixed(2),
  growth_rate_pct: (growthRate * 100).toFixed(1),
  weighted_pipeline: weightedPipeline.toFixed(2),
  historical_months: String(revenueValues.length),
  pipeline_deal_count: String(deals.length),
  forecast_confidence: confidence,
  is_complex_request: isComplex
};`,
          outputVariables: [
            { name: "forecast_summary", type: "string" },
            { name: "total_forecast", type: "string" },
            { name: "avg_monthly_historical", type: "string" },
            { name: "growth_rate_pct", type: "string" },
            { name: "weighted_pipeline", type: "string" },
            { name: "historical_months", type: "string" },
            { name: "pipeline_deal_count", type: "string" },
            { name: "forecast_confidence", type: "string" },
            { name: "is_complex_request", type: "string" },
          ],
        },
      }),

      // 6. AI confidence + commentary
      aiNode(
        "6",
        "AI: Add forecast commentary & insights",
        1240,
        "You are a financial analyst. Given the forecast data, provide executive commentary including: key drivers, risk factors, assumptions, and recommended actions. Note the confidence level and explain what would improve accuracy. Respond JSON: {commentary: string, key_risks: string, assumptions_used: string, recommended_actions: string, executive_one_liner: string}",
        "Forecast period: {forecast_period} ({forecast_months} months, {granularity}). Segments: {segments}. Assumptions: {specific_assumptions}. Historical avg monthly: ${avg_monthly_historical} over {historical_months} months. Growth rate: {growth_rate_pct}%. Weighted pipeline: ${weighted_pipeline} from {pipeline_deal_count} deals. Confidence: {forecast_confidence}.\\n\\nForecast:\\n{forecast_summary}\\nTotal: ${total_forecast}",
        [
          { field: "commentary", as: "forecast_commentary", type: "string" },
          { field: "key_risks", as: "key_risks", type: "string" },
          { field: "assumptions_used", as: "assumptions_used", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "executive_one_liner",
            as: "executive_one_liner",
            type: "string",
          },
        ],
      ),

      // 7. Conversational delivery (Slack)
      slackNode(
        "7",
        "Slack: Deliver forecast to requester",
        1480,
        ":crystal_ball: *Revenue Forecast: {forecast_period}*\\n_{executive_one_liner}_\\n\\n*Forecast ({forecast_confidence} confidence):*\\n{forecast_summary}\\n*Total: ${total_forecast}*\\n\\n*Drivers:* Avg monthly: ${avg_monthly_historical} | Growth: {growth_rate_pct}% | Pipeline: ${weighted_pipeline}\\n\\n*Commentary:* {forecast_commentary}\\n*Risks:* {key_risks}\\n*Actions:* {recommended_actions}",
      ),

      // 8. FP&A escalation condition
      n("8", "condition", "Complex request needing FP&A review?", 1720, {
        conditionData: [
          {
            variable: "is_complex_request",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      // 8 is a terminal condition — complex requests can be flagged for FP&A follow-up
    ],
  },

  // ===================================================================
  // 662. REVENUE LEAKAGE DETECTOR (9 nodes)
  // ===================================================================

  "Revenue Leakage Detector": {
    summary:
      "Weekly scan that compares contract terms against billed invoices and usage data to identify revenue leakage. A weekly schedule triggers contract terms pull, billed invoices are pulled from Stripe, usage data is pulled, leakage identification runs via runCode, a materiality condition filters significant leaks, a ranked leakage report is written to Sheets, finance leadership receives an email, and critical leaks alert Slack.",
    nodes: [
      trigger("Schedule: Weekly leakage scan", "schedule", {
        schedule: "0 7 * * 1",
        howItStarts:
          "Runs every Monday at 7 AM to scan for revenue leakage across all active contracts",
        setup:
          "Ensure contract terms are stored in Sheets with columns: contract_id, customer, monthly_rate, usage_limit, overage_rate, start_date, end_date. Connect Stripe for invoice data.",
        inputFields: [],
      }),

      // 2. Contract terms pull (Sheets)
      n("2", "hitApi", "Fetch active contract terms from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Contracts!A:H",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values",
              customName: "contract_terms_raw",
              type: "string",
            },
          ],
        },
      }),

      // 3. Billed invoice pull (Stripe)
      stripeNode(
        "3",
        "Stripe: Pull last week invoices",
        520,
        "GET",
        "invoices?created[gte]={week_ago_epoch}&status=paid&limit=100",
        [],
        [{ name: "data", customName: "paid_invoices_json", type: "string" }],
      ),

      // 4. Usage data pull (from usage tracking Sheets)
      n("4", "hitApi", "Fetch usage data from tracking sheet", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/UsageTracking!A:E",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "usage_data_raw", type: "string" },
          ],
        },
      }),

      // 5. Leakage identification (runCode)
      n("5", "runCode", "Identify revenue leakage", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare contracts vs invoices vs usage to find leakage
const contracts = JSON.parse(vars.contract_terms_raw || "[]");
const invoices = JSON.parse(vars.paid_invoices_json || "[]");
const usage = JSON.parse(vars.usage_data_raw || "[]");

// Build invoice lookup by customer
const invoiceByCustomer = {};
for (const inv of invoices) {
  const cust = inv.customer || "";
  if (!invoiceByCustomer[cust]) invoiceByCustomer[cust] = [];
  invoiceByCustomer[cust].push(inv);
}

// Build usage lookup by contract
const usageByContract = {};
for (const row of usage) {
  const contractId = row[0];
  usageByContract[contractId] = parseFloat(row[2]) || 0;
}

const leaks = [];
let totalLeakage = 0;

for (const row of contracts) {
  if (!row[0] || row[0] === "contract_id") continue; // skip header
  const contractId = row[0];
  const customer = row[1] || "";
  const monthlyRate = parseFloat(row[2]) || 0;
  const usageLimit = parseFloat(row[3]) || 0;
  const overageRate = parseFloat(row[4]) || 0;
  const customerId = row[5] || "";

  const custInvoices = invoiceByCustomer[customerId] || [];
  const totalBilled = custInvoices.reduce((s, i) => s + ((i.amount_paid || 0) / 100), 0);
  const actualUsage = usageByContract[contractId] || 0;

  // Check for underbilling
  const expectedBase = monthlyRate;
  const overageUnits = Math.max(actualUsage - usageLimit, 0);
  const expectedOverage = overageUnits * overageRate;
  const expectedTotal = expectedBase + expectedOverage;
  const leakAmount = expectedTotal - totalBilled;

  if (leakAmount > 1) { // $1 threshold
    totalLeakage += leakAmount;
    leaks.push(contractId + " | " + customer + " | Expected: $" + expectedTotal.toFixed(2) + " | Billed: $" + totalBilled.toFixed(2) + " | Leak: $" + leakAmount.toFixed(2) + " | Usage: " + actualUsage + "/" + usageLimit);
  }
}

leaks.sort((a, b) => {
  const amtA = parseFloat(a.split("Leak: $")[1]) || 0;
  const amtB = parseFloat(b.split("Leak: $")[1]) || 0;
  return amtB - amtA;
});

const isMaterial = totalLeakage > 100 ? "yes" : "no";

return {
  leakage_report: leaks.join("\\n") || "No leakage detected",
  total_leakage: totalLeakage.toFixed(2),
  leak_count: String(leaks.length),
  contracts_scanned: String(contracts.length - 1),
  is_material: isMaterial,
  top_leak: leaks[0] || "None"
};`,
          outputVariables: [
            { name: "leakage_report", type: "string" },
            { name: "total_leakage", type: "string" },
            { name: "leak_count", type: "string" },
            { name: "contracts_scanned", type: "string" },
            { name: "is_material", type: "string" },
            { name: "top_leak", type: "string" },
          ],
        },
      }),

      // 6. Materiality condition
      n("6", "condition", "Leakage material (> $100)?", 1240, {
        conditionData: [
          {
            variable: "is_material",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Ranked leakage report (Sheets)
      sheetsNode(
        "7",
        "Write leakage report to Sheets",
        1480,
        "RevenueLeakage",
        `[["${new Date().toISOString().slice(0, 10)}","{contracts_scanned}","{leak_count}","{total_leakage}","{top_leak}","${new Date().toISOString()}"]]`,
      ),

      // 8. Finance leadership email
      emailNode(
        "8",
        "Send leakage report to finance leadership",
        1720,
        "{finance_leadership_email}",
        "Weekly Revenue Leakage Report — ${total_leakage} identified",
        "Revenue Leakage Report\\n\\nContracts Scanned: {contracts_scanned}\\nLeaks Identified: {leak_count}\\nTotal Leakage: ${total_leakage}\\n\\n--- Ranked Leakage Details ---\\n{leakage_report}\\n\\nPlease review and action the top leaks. Full details are in the RevenueLeakage sheet.",
      ),

      // 9. Critical Slack alert
      slackNode(
        "9",
        "Slack: Critical leakage alert",
        1480,
        ":money_with_wings: *Revenue Leakage Detected*\\nContracts: {contracts_scanned} scanned | Leaks: {leak_count} found\\nTotal Leakage: *${total_leakage}*\\n\\nTop Leak: {top_leak}\\n\\nFull report sent to finance leadership.",
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // material -> report + alert
      e("6", "7", "output_2"), // not material -> still report
      e("7", "8"),
      e("7", "9"), // parallel alert
    ],
  },

  // ===================================================================
  // 663. REVENUE RECOGNITION AUTOMATION (10 nodes)
  // ===================================================================

  "Revenue Recognition Automation": {
    summary:
      "Automates ASC 606 revenue recognition with multi-element allocation, deferred revenue booking, and journal entries. A payment/invoice trigger starts the flow, contract terms are pulled, a recognition schedule is determined via runCode, multi-element allocation splits revenue, deferred revenue is booked, monthly earned recognition processes, journal entries are created, an audit trail writes to Sheets, a quarter-end summary email is sent, and ambiguous terms alert Slack.",
    nodes: [
      trigger("Webhook: Payment or invoice received", "webhook_app", {
        source: "Stripe",
        event: "invoice.payment_succeeded",
        howItStarts:
          "Stripe fires a webhook when an invoice payment succeeds, triggering revenue recognition processing",
        setup:
          "Configure Stripe webhook for invoice.payment_succeeded. Ensure invoices include subscription metadata with contract terms, performance obligations, and delivery schedule.",
        inputFields: [],
      }),

      // 2. Contract terms pull
      aiNode(
        "2",
        "AI: Extract contract & recognition terms",
        280,
        "You are an ASC 606 revenue recognition specialist. Parse the payment webhook to extract all fields needed for revenue recognition: customer, invoice details, subscription terms, performance obligations, delivery schedule, contract start/end dates. Determine if the arrangement has multiple elements. Respond JSON: {customer_name: string, customer_email: string, invoice_id: string, total_amount: number, currency: string, contract_start: string, contract_end: string, contract_months: number, has_multiple_elements: string (yes|no), elements: string, performance_obligations: string, delivery_type: string (point_in_time|over_time|mixed)}",
        "Payment webhook: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "invoice_id", as: "invoice_id", type: "string" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "contract_start", as: "contract_start", type: "string" },
          { field: "contract_end", as: "contract_end", type: "string" },
          { field: "contract_months", as: "contract_months", type: "number" },
          {
            field: "has_multiple_elements",
            as: "has_multiple_elements",
            type: "string",
          },
          { field: "elements", as: "elements_json", type: "string" },
          {
            field: "performance_obligations",
            as: "performance_obligations",
            type: "string",
          },
          { field: "delivery_type", as: "delivery_type", type: "string" },
        ],
      ),

      // 3. Recognition schedule determination (runCode)
      n("3", "runCode", "Determine recognition schedule", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate revenue recognition schedule per ASC 606
const totalAmount = parseFloat(vars.total_amount) || 0;
const months = parseInt(vars.contract_months) || 1;
const deliveryType = vars.delivery_type || "over_time";
const hasMultiple = vars.has_multiple_elements === "yes";
const start = vars.contract_start || new Date().toISOString().slice(0, 10);

let recognizedNow = 0;
let deferred = 0;
let monthlyRecognition = 0;

if (deliveryType === "point_in_time") {
  recognizedNow = totalAmount;
  deferred = 0;
  monthlyRecognition = 0;
} else if (deliveryType === "over_time") {
  recognizedNow = 0;
  deferred = totalAmount;
  monthlyRecognition = (totalAmount / months);
} else { // mixed
  recognizedNow = totalAmount * 0.3; // 30% upfront for setup
  deferred = totalAmount * 0.7;
  monthlyRecognition = deferred / months;
}

const schedule = [];
const startDate = new Date(start);
for (let i = 0; i < Math.min(months, 12); i++) {
  const monthDate = new Date(startDate);
  monthDate.setMonth(monthDate.getMonth() + i);
  const amt = i === 0 ? recognizedNow + monthlyRecognition : monthlyRecognition;
  schedule.push(monthDate.toISOString().slice(0, 7) + ": $" + amt.toFixed(2));
}

const isAmbiguous = hasMultiple && deliveryType === "mixed" ? "yes" : "no";

return {
  recognized_now: recognizedNow.toFixed(2),
  deferred_amount: deferred.toFixed(2),
  monthly_recognition: monthlyRecognition.toFixed(2),
  recognition_schedule: schedule.join("\\n"),
  schedule_months: String(months),
  is_ambiguous: isAmbiguous,
  recognition_method: deliveryType
};`,
          outputVariables: [
            { name: "recognized_now", type: "string" },
            { name: "deferred_amount", type: "string" },
            { name: "monthly_recognition", type: "string" },
            { name: "recognition_schedule", type: "string" },
            { name: "schedule_months", type: "string" },
            { name: "is_ambiguous", type: "string" },
            { name: "recognition_method", type: "string" },
          ],
        },
      }),

      // 4. Multi-element allocation (runCode)
      n("4", "runCode", "Allocate multi-element arrangement", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Allocate transaction price to performance obligations
const elements = JSON.parse(vars.elements_json || "[]");
const totalAmount = parseFloat(vars.total_amount) || 0;
const hasMultiple = vars.has_multiple_elements === "yes";

if (!hasMultiple || elements.length <= 1) {
  return {
    allocation_summary: "Single element: full amount $" + totalAmount.toFixed(2),
    element_count: "1",
    allocation_method: "single_element"
  };
}

// Relative standalone selling price allocation
const totalSSP = elements.reduce((s, e) => s + (parseFloat(e.standalone_price) || 0), 0);
const allocations = elements.map(el => {
  const ssp = parseFloat(el.standalone_price) || 0;
  const allocated = totalSSP > 0 ? (ssp / totalSSP) * totalAmount : totalAmount / elements.length;
  return (el.name || "Element") + ": $" + allocated.toFixed(2) + " (SSP: $" + ssp.toFixed(2) + ")";
});

return {
  allocation_summary: allocations.join("\\n"),
  element_count: String(elements.length),
  allocation_method: "relative_ssp"
};`,
          outputVariables: [
            { name: "allocation_summary", type: "string" },
            { name: "element_count", type: "string" },
            { name: "allocation_method", type: "string" },
          ],
        },
      }),

      // 5. Deferred revenue booking (Sheets)
      sheetsNode(
        "5",
        "Book deferred revenue entry",
        1000,
        "DeferredRevenue",
        `[["{invoice_id}","{customer_name}","{total_amount}","{deferred_amount}","{recognized_now}","{contract_start}","{contract_end}","{recognition_method}","{allocation_method}","${new Date().toISOString()}"]]`,
      ),

      // 6. Monthly earned recognition (journal entry prep)
      n("6", "runCode", "Prepare monthly journal entries", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Create journal entry lines for this period recognition
const recognizedNow = parseFloat(vars.recognized_now) || 0;
const monthlyAmt = parseFloat(vars.monthly_recognition) || 0;
const currency = vars.currency || "USD";
const entries = [];

if (recognizedNow > 0) {
  entries.push("DR Revenue Receivable $" + recognizedNow.toFixed(2));
  entries.push("CR Revenue - Earned $" + recognizedNow.toFixed(2));
}
if (parseFloat(vars.deferred_amount) > 0) {
  entries.push("DR Cash/AR $" + vars.deferred_amount);
  entries.push("CR Deferred Revenue $" + vars.deferred_amount);
}
entries.push("Monthly: DR Deferred Revenue $" + monthlyAmt.toFixed(2) + " / CR Revenue - Earned $" + monthlyAmt.toFixed(2));

return {
  journal_entries: entries.join("\\n"),
  current_period_revenue: recognizedNow.toFixed(2),
  journal_entry_count: String(entries.length)
};`,
          outputVariables: [
            { name: "journal_entries", type: "string" },
            { name: "current_period_revenue", type: "string" },
            { name: "journal_entry_count", type: "string" },
          ],
        },
      }),

      // 7. Journal entry creation (QuickBooks)
      quickbooksNode(
        "7",
        "QuickBooks: Create journal entry",
        1480,
        "POST",
        "journalentry",
        [
          {
            key: "JournalEntry",
            value: `{"Line":[{"DetailType":"JournalEntryLineDetail","Amount":{recognized_now},"JournalEntryLineDetail":{"PostingType":"Debit","AccountRef":{"value":"1"}}}],"PrivateNote":"Rev Rec: {invoice_id} - {customer_name}"}`,
          },
        ],
        [
          {
            name: "JournalEntry.Id",
            customName: "journal_entry_id",
            type: "string",
          },
        ],
      ),

      // 8. Audit trail (Sheets)
      sheetsNode(
        "8",
        "Log recognition audit trail",
        1720,
        "RevRecAudit",
        `[["{invoice_id}","{customer_name}","{total_amount}","{recognized_now}","{deferred_amount}","{monthly_recognition}","{recognition_method}","{allocation_summary}","{journal_entry_id}","${new Date().toISOString()}"]]`,
      ),

      // 9. Quarter-end summary email
      emailNode(
        "9",
        "Send quarter-end recognition summary",
        1960,
        "{finance_team_email}",
        "Revenue Recognition: {customer_name} — {currency} {total_amount} ({recognition_method})",
        "Revenue Recognition Summary\\n\\nCustomer: {customer_name} ({customer_email})\\nInvoice: {invoice_id}\\nTotal: {currency} {total_amount}\\n\\nRecognition Method: {recognition_method}\\nRecognized Now: {currency} {recognized_now}\\nDeferred: {currency} {deferred_amount}\\nMonthly Recognition: {currency} {monthly_recognition}\\n\\nAllocation:\\n{allocation_summary}\\n\\nSchedule ({schedule_months} months):\\n{recognition_schedule}\\n\\nJournal Entries:\\n{journal_entries}",
      ),

      // 10. Ambiguous terms Slack alert
      slackNode(
        "10",
        "Slack: Ambiguous recognition terms alert",
        1000,
        ":warning: *Revenue Recognition — Ambiguous Terms*\\nInvoice: {invoice_id} | Customer: {customer_name}\\nAmount: {currency} {total_amount} | Elements: {element_count}\\nDelivery: {delivery_type} | Obligations: {performance_obligations}\\n\\nThis arrangement has mixed delivery elements requiring manual review of the SSP allocation.",
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      // Ambiguous branch: check from node 3
      e("3", "10"), // ambiguous flag set in node 3 -> Slack alert in parallel
    ],
  },

  // ===================================================================
  // 664. REVENUE RECOGNITION TRACKER (7 nodes)
  // ===================================================================

  "Revenue Recognition Tracker": {
    summary:
      "Tracks Stripe payment events, classifies them as new/renewal/expansion, stores events, aggregates MRR/ARR, and reports to accounting. A Stripe payment event triggers classification via runCode, the event is stored in Sheets, monthly aggregation computes MRR/ARR, an MRR/ARR waterfall is built, an accounting email summary is sent, and a revenue channel Slack post delivers the update.",
    nodes: [
      trigger("Webhook: Stripe payment event", "webhook_app", {
        source: "Stripe",
        event: "invoice.payment_succeeded",
        howItStarts:
          "Stripe fires a webhook when any invoice payment succeeds, triggering revenue classification and tracking",
        setup:
          "Configure Stripe webhook for invoice.payment_succeeded events. Ensure subscriptions include metadata for plan tier, customer segment, and previous MRR.",
        inputFields: [],
      }),

      // 2. Classification (runCode: new/renewal/expansion)
      n("2", "runCode", "Classify revenue: new, renewal, or expansion", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify Stripe payment as new, renewal, or expansion
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const invoice_id = data.id || "";
const customer_email = data.customer_email || "";
const customer_name = data.customer_name || "";
const amount = ((data.amount_paid || data.amount_due || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const subscription_id = data.subscription || "";
const period_start = new Date((data.period_start || 0) * 1000).toISOString().slice(0, 10);
const period_end = new Date((data.period_end || 0) * 1000).toISOString().slice(0, 10);
const billing_reason = data.billing_reason || "";
const previousMRR = parseFloat((data.metadata || {}).previous_mrr || "0");
const currentMRR = parseFloat(amount);
const plan = (data.lines?.data?.[0]?.plan?.nickname) || (data.metadata || {}).plan_name || "Standard";

let classification = "new";
if (billing_reason === "subscription_cycle") {
  classification = currentMRR > previousMRR ? "expansion" : "renewal";
} else if (billing_reason === "subscription_update") {
  classification = "expansion";
} else if (billing_reason === "subscription_create") {
  classification = "new";
}

const mrrDelta = currentMRR - previousMRR;
const arrImpact = (mrrDelta * 12).toFixed(2);
const month = new Date().toISOString().slice(0, 7);

return {
  invoice_id, customer_email, customer_name, amount, currency,
  subscription_id, period_start, period_end, plan,
  classification, previous_mrr: previousMRR.toFixed(2),
  current_mrr: currentMRR.toFixed(2), mrr_delta: mrrDelta.toFixed(2),
  arr_impact: arrImpact, event_month: month
};`,
          outputVariables: [
            { name: "invoice_id", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "subscription_id", type: "string" },
            { name: "period_start", type: "string" },
            { name: "period_end", type: "string" },
            { name: "plan", type: "string" },
            { name: "classification", type: "string" },
            { name: "previous_mrr", type: "string" },
            { name: "current_mrr", type: "string" },
            { name: "mrr_delta", type: "string" },
            { name: "arr_impact", type: "string" },
            { name: "event_month", type: "string" },
          ],
        },
      }),

      // 3. Event store write (Sheets)
      sheetsNode(
        "3",
        "Store revenue event",
        520,
        "RevenueEvents",
        `[["{invoice_id}","{customer_name}","{customer_email}","{classification}","{amount}","{currency}","{plan}","{previous_mrr}","{current_mrr}","{mrr_delta}","{period_start}","{period_end}","${new Date().toISOString()}"]]`,
      ),

      // 4. Monthly aggregation (runCode)
      n("4", "runCode", "Aggregate monthly MRR waterfall", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build MRR waterfall entry
const classification = vars.classification || "new";
const mrrDelta = parseFloat(vars.mrr_delta) || 0;
const currentMRR = parseFloat(vars.current_mrr) || 0;
const month = vars.event_month || new Date().toISOString().slice(0, 7);

const newMRR = classification === "new" ? currentMRR : 0;
const expansionMRR = classification === "expansion" ? mrrDelta : 0;
const renewalMRR = classification === "renewal" ? currentMRR : 0;
const totalMRR = currentMRR;
const totalARR = (totalMRR * 12).toFixed(2);

const waterfallLine = month + " | New: $" + newMRR.toFixed(2) + " | Expansion: $" + expansionMRR.toFixed(2) + " | Renewal: $" + renewalMRR.toFixed(2) + " | Net MRR: $" + totalMRR.toFixed(2) + " | ARR: $" + totalARR;

return {
  new_mrr: newMRR.toFixed(2),
  expansion_mrr: expansionMRR.toFixed(2),
  renewal_mrr: renewalMRR.toFixed(2),
  total_mrr: totalMRR.toFixed(2),
  total_arr: totalARR,
  waterfall_line: waterfallLine
};`,
          outputVariables: [
            { name: "new_mrr", type: "string" },
            { name: "expansion_mrr", type: "string" },
            { name: "renewal_mrr", type: "string" },
            { name: "total_mrr", type: "string" },
            { name: "total_arr", type: "string" },
            { name: "waterfall_line", type: "string" },
          ],
        },
      }),

      // 5. MRR/ARR waterfall (Sheets)
      sheetsNode(
        "5",
        "Update MRR/ARR waterfall",
        1000,
        "MRRWaterfall",
        `[["{event_month}","{classification}","{customer_name}","{new_mrr}","{expansion_mrr}","{renewal_mrr}","{total_mrr}","{total_arr}","${new Date().toISOString()}"]]`,
      ),

      // 6. Accounting email summary
      emailNode(
        "6",
        "Send accounting revenue event summary",
        1240,
        "{accounting_email}",
        "Revenue Event: {classification} — {customer_name} ({currency} {amount})",
        "Revenue Event Notification\\n\\nType: {classification}\\nCustomer: {customer_name} ({customer_email})\\nAmount: {currency} {amount}\\nPlan: {plan}\\n\\nMRR Impact:\\nPrevious: {currency} {previous_mrr}\\nCurrent: {currency} {current_mrr}\\nDelta: {currency} {mrr_delta}\\n\\nARR Impact: {currency} {arr_impact}\\n\\nWaterfall: {waterfall_line}",
      ),

      // 7. Revenue channel Slack post
      slackNode(
        "7",
        "Slack: Revenue event update",
        1480,
        ":moneybag: *Revenue Event: {classification}*\\nCustomer: {customer_name}\\nAmount: {currency} {amount} | Plan: {plan}\\nMRR: {currency} {previous_mrr} -> {currency} {current_mrr} (delta: {currency} {mrr_delta})\\nARR Impact: {currency} {arr_impact}\\n\\n{waterfall_line}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 665. SALES TAX CALCULATION BOT (8 nodes)
  // ===================================================================

  "Sales Tax Calculation Bot": {
    summary:
      "Conversational bot that answers sales tax questions with jurisdiction-aware rate lookups and nexus checks. A chat trigger starts the flow, AI classifies the question, jurisdiction is extracted, tax rates are looked up, a nexus check condition validates applicability, AI generates a disclaimered answer, leads are captured in HubSpot, and complex tax questions escalate to a specialist.",
    nodes: [
      trigger("Webhook: Sales tax question received", "webhook_app", {
        source: "Chat Widget / Portal",
        event: "tax.question.received",
        howItStarts:
          "A customer or internal user submits a sales tax question via chat widget or portal",
        setup:
          "Configure chat widget or portal to POST tax questions to this workflow URL. Include the user email, question text, and any product/location context.",
        inputFields: [],
      }),

      // 2. AI question classification
      aiNode(
        "2",
        "AI: Classify tax question & extract context",
        280,
        "You are a sales tax specialist. Analyze the tax question to classify it and extract relevant context. Identify: question type (rate_lookup, nexus_check, exemption, filing, general), product/service category, origin location, destination location, and transaction amount if mentioned. Respond JSON: {question_type: string, product_category: string, origin_state: string, destination_state: string, destination_zip: string, transaction_amount: number, user_email: string, user_name: string, is_complex: string (yes|no), parsed_question: string}",
        "Tax question: {webhook_payload}",
        [
          { field: "question_type", as: "question_type", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "origin_state", as: "origin_state", type: "string" },
          {
            field: "destination_state",
            as: "destination_state",
            type: "string",
          },
          { field: "destination_zip", as: "destination_zip", type: "string" },
          {
            field: "transaction_amount",
            as: "transaction_amount",
            type: "number",
          },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "is_complex", as: "is_complex", type: "string" },
          { field: "parsed_question", as: "parsed_question", type: "string" },
        ],
      ),

      // 3. Jurisdiction extraction & tax rate lookup (TaxJar API)
      n("3", "hitApi", "TaxJar: Look up tax rates", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.taxjar.com/v2/rates/{destination_zip}",
          headers: [
            { key: "Authorization", value: "Bearer {taxjar_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "rate.combined_rate",
              customName: "combined_rate",
              type: "string",
            },
            {
              name: "rate.state_rate",
              customName: "state_rate",
              type: "string",
            },
            {
              name: "rate.county_rate",
              customName: "county_rate",
              type: "string",
            },
            { name: "rate.city_rate", customName: "city_rate", type: "string" },
            { name: "rate.state", customName: "rate_state", type: "string" },
            { name: "rate.county", customName: "rate_county", type: "string" },
            { name: "rate.city", customName: "rate_city", type: "string" },
          ],
        },
      }),

      // 4. Nexus check (TaxJar)
      n("4", "hitApi", "TaxJar: Nexus check", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.taxjar.com/v2/nexus/regions",
          headers: [
            { key: "Authorization", value: "Bearer {taxjar_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "regions",
              customName: "nexus_regions_json",
              type: "string",
            },
          ],
        },
      }),

      // 5. Nexus check condition (runCode + condition)
      n("5", "runCode", "Determine nexus applicability", 940, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if destination state is in nexus regions
const regions = JSON.parse(vars.nexus_regions_json || "[]");
const destState = (vars.destination_state || "").toUpperCase();
const hasNexus = regions.some(r => (r.region_code || "").toUpperCase() === destState) ? "yes" : "no";
const combinedRate = parseFloat(vars.combined_rate) || 0;
const txnAmount = parseFloat(vars.transaction_amount) || 0;
const estimatedTax = (txnAmount * combinedRate).toFixed(2);
const totalWithTax = (txnAmount + parseFloat(estimatedTax)).toFixed(2);
return {
  has_nexus: hasNexus,
  estimated_tax: estimatedTax,
  total_with_tax: totalWithTax,
  effective_rate_pct: (combinedRate * 100).toFixed(3)
};`,
          outputVariables: [
            { name: "has_nexus", type: "string" },
            { name: "estimated_tax", type: "string" },
            { name: "total_with_tax", type: "string" },
            { name: "effective_rate_pct", type: "string" },
          ],
        },
      }),

      // 6. Nexus condition
      n("6", "condition", "Has nexus in destination state?", 1120, {
        conditionData: [
          {
            variable: "has_nexus",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. AI answer with disclaimers
      aiNode(
        "7",
        "AI: Generate tax answer with disclaimers",
        1360,
        "You are a sales tax advisor. Using the actual tax rate data and nexus information, provide a clear, accurate answer to the customer question. Always include: the specific rate breakdown, jurisdiction details, and a disclaimer that this is informational only and not tax advice. If nexus does not apply, explain why the tax may not be applicable. Respond JSON: {answer: string, rate_breakdown: string, disclaimer: string, confidence: string}",
        "Question: {parsed_question}. Type: {question_type}. Product: {product_category}. Destination: {rate_state} ({rate_county}, {rate_city}), ZIP: {destination_zip}. Nexus: {has_nexus}. Combined rate: {effective_rate_pct}% (State: {state_rate}, County: {county_rate}, City: {city_rate}). Transaction: ${transaction_amount}. Estimated tax: ${estimated_tax}. Total: ${total_with_tax}.",
        [
          { field: "answer", as: "tax_answer", type: "string" },
          { field: "rate_breakdown", as: "rate_breakdown", type: "string" },
          { field: "disclaimer", as: "tax_disclaimer", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "string" },
        ],
      ),

      // 8. Lead capture (HubSpot) + complex escalation
      hubspotNode(
        "8",
        "HubSpot: Capture tax inquiry lead",
        1600,
        "POST",
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value: `{"email":"{user_email}","firstname":"{user_name}","hs_lead_status":"OPEN","tax_question_type":"{question_type}","tax_jurisdiction":"{rate_state}"}`,
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // has nexus -> full answer with rates
      e("6", "7", "output_2"), // no nexus -> answer explaining non-applicability
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 666. SHOPIFY ORDER TO QUICKBOOKS INVOICE (9 nodes)
  // ===================================================================

  "Shopify Order to QuickBooks Invoice": {
    summary:
      "Syncs Shopify orders to QuickBooks invoices with customer matching, SKU mapping, and payment posting. A Shopify order webhook triggers data normalization via runCode, QB customer find/create resolves the customer, a SKU mapping condition verifies product mapping, a QuickBooks invoice is created, payment is posted, Shopify order is tagged as synced, unmapped SKUs alert Slack, and sync events are logged to Sheets.",
    nodes: [
      trigger("Webhook: Shopify order created", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts:
          "Shopify fires a webhook when a new order is placed in the store",
        setup:
          "In Shopify Admin -> Settings -> Notifications -> Webhooks, create a webhook for 'Order creation' pointing to your workflow URL. Use JSON format.",
        inputFields: [],
      }),

      // 2. Data normalization (runCode)
      n("2", "runCode", "Normalize Shopify order data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Shopify order webhook payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const order_id = String(payload.id || payload.order_number || "");
const order_number = String(payload.order_number || payload.name || "");
const customer = payload.customer || {};
const customer_email = customer.email || payload.email || "";
const customer_name = (customer.first_name || "") + " " + (customer.last_name || "");
const billing = payload.billing_address || {};
const billing_address = [billing.address1, billing.city, billing.province_code, billing.zip, billing.country_code].filter(Boolean).join(", ");
const currency = payload.currency || "USD";
const subtotal = parseFloat(payload.subtotal_price || 0).toFixed(2);
const tax_total = parseFloat(payload.total_tax || 0).toFixed(2);
const total = parseFloat(payload.total_price || 0).toFixed(2);
const shipping = parseFloat((payload.total_shipping_price_set?.shop_money?.amount) || 0).toFixed(2);
const financial_status = payload.financial_status || "pending";

// Extract line items
const lineItems = (payload.line_items || []).map(li => ({
  sku: li.sku || "",
  name: li.name || li.title || "",
  quantity: li.quantity || 1,
  price: parseFloat(li.price || 0).toFixed(2),
  total: (li.quantity * parseFloat(li.price || 0)).toFixed(2)
}));
const formattedItems = lineItems.map(li => li.sku + " | " + li.name + " x" + li.quantity + " @ $" + li.price).join("\\n");
const allSkusMapped = lineItems.every(li => li.sku && li.sku.length > 0) ? "yes" : "no";
const unmappedSkus = lineItems.filter(li => !li.sku || li.sku.length === 0).map(li => li.name).join(", ");

return {
  order_id, order_number, customer_email, customer_name: customer_name.trim(),
  billing_address, currency, subtotal, tax_total, total, shipping,
  financial_status, line_items_json: JSON.stringify(lineItems),
  formatted_items: formattedItems, item_count: String(lineItems.length),
  all_skus_mapped: allSkusMapped, unmapped_skus: unmappedSkus
};`,
          outputVariables: [
            { name: "order_id", type: "string" },
            { name: "order_number", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "billing_address", type: "string" },
            { name: "currency", type: "string" },
            { name: "subtotal", type: "string" },
            { name: "tax_total", type: "string" },
            { name: "total", type: "string" },
            { name: "shipping", type: "string" },
            { name: "financial_status", type: "string" },
            { name: "line_items_json", type: "string" },
            { name: "formatted_items", type: "string" },
            { name: "item_count", type: "string" },
            { name: "all_skus_mapped", type: "string" },
            { name: "unmapped_skus", type: "string" },
          ],
        },
      }),

      // 3. QB customer find/create
      quickbooksNode(
        "3",
        "QuickBooks: Find or create customer",
        520,
        "POST",
        "query?query=SELECT * FROM Customer WHERE PrimaryEmailAddr = '{customer_email}'",
        [],
        [
          {
            name: "QueryResponse.Customer[0].Id",
            customName: "qb_customer_id",
            type: "string",
          },
          {
            name: "QueryResponse.totalCount",
            customName: "qb_customer_found",
            type: "number",
          },
        ],
      ),

      // 4. SKU mapping condition
      n("4", "condition", "All SKUs mapped to QB items?", 760, {
        conditionData: [
          {
            variable: "all_skus_mapped",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Invoice creation (QuickBooks)
      quickbooksNode(
        "5",
        "QuickBooks: Create invoice from order",
        1000,
        "POST",
        "invoice",
        [
          {
            key: "Invoice",
            value: `{"CustomerRef":{"value":"{qb_customer_id}"},"BillEmail":{"Address":"{customer_email}"},"Line":{line_items_json},"TxnDate":"${new Date().toISOString().slice(0, 10)}","PrivateNote":"Shopify Order #{order_number}","CurrencyRef":{"value":"{currency}"},"TotalAmt":{total}}`,
          },
        ],
        [
          { name: "Invoice.Id", customName: "qb_invoice_id", type: "string" },
          {
            name: "Invoice.DocNumber",
            customName: "qb_invoice_number",
            type: "string",
          },
        ],
      ),

      // 6. Payment posting (QuickBooks)
      quickbooksNode(
        "6",
        "QuickBooks: Post payment",
        1240,
        "POST",
        "payment",
        [
          {
            key: "Payment",
            value: `{"CustomerRef":{"value":"{qb_customer_id}"},"TotalAmt":{total},"Line":[{"Amount":{total},"LinkedTxn":[{"TxnId":"{qb_invoice_id}","TxnType":"Invoice"}]}],"PrivateNote":"Shopify #{order_number} - {financial_status}"}`,
          },
        ],
        [{ name: "Payment.Id", customName: "qb_payment_id", type: "string" }],
      ),

      // 7. Shopify order tagging
      n("7", "hitApi", "Shopify: Tag order as synced", 1480, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "order",
              value: `{"id":"{order_id}","tags":"qb-synced,invoice-{qb_invoice_number}"}`,
            },
          ],
          variables: [
            { name: "order.tags", customName: "updated_tags", type: "string" },
          ],
        },
      }),

      // 8. Unmapped SKU Slack alert
      slackNode(
        "8",
        "Slack: Unmapped SKU alert",
        1000,
        ":warning: *Unmapped SKU Alert*\\nShopify Order #{order_number} has unmapped SKUs that could not be synced to QuickBooks.\\n\\nUnmapped items: {unmapped_skus}\\nTotal items: {item_count}\\nOrder total: {currency} {total}\\n\\nPlease create the missing item mappings and manually sync this order.",
        480,
      ),

      // 9. Sync logging (Sheets)
      sheetsNode(
        "9",
        "Log Shopify-QB sync event",
        1720,
        "ShopifyQBSync",
        `[["{order_number}","{customer_name}","{customer_email}","{total}","{currency}","{item_count}","{all_skus_mapped}","{qb_invoice_number}","{qb_payment_id}","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // all mapped -> create invoice
      e("4", "8", "output_2"), // unmapped -> Slack alert
      e("5", "6"), // invoice -> payment
      e("6", "7"), // payment -> tag Shopify
      e("7", "9"), // tag -> log
      e("8", "9"), // alert also logs
    ],
  },

  // ===================================================================
  // 667. SPONSORSHIP RENEWAL (8 nodes)
  // ===================================================================

  "Sponsorship Renewal": {
    summary:
      "Proactive sponsorship renewal flow with AI impact reports, package recommendations, and scarcity follow-ups. A 60-day expiry trigger scans for expiring sponsorships, sponsor terms and performance data are pulled, AI generates an impact report, a package recommendation is created, a renewal proposal email is sent, a follow-up condition checks engagement, a scarcity follow-up email creates urgency, and the sponsorship owner is notified on Slack.",
    nodes: [
      trigger("Schedule: 60-day sponsorship expiry scan", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs every Monday at 8 AM to find sponsorships expiring within 60 days and initiate renewal outreach",
        setup:
          "Ensure sponsorship data is stored in Sheets with columns: sponsor_name, sponsor_email, package, amount, start_date, end_date, impressions, clicks, leads_generated, account_manager_email. Configure the scan window for 60 days out.",
        inputFields: [],
      }),

      // 2. Sponsor terms/performance pull (Sheets)
      n("2", "hitApi", "Fetch expiring sponsorships from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Sponsorships!A:K",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values",
              customName: "sponsorship_data_raw",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI impact report
      aiNode(
        "3",
        "AI: Generate sponsorship impact report",
        520,
        "You are a sponsorship success manager. Given the sponsor data including impressions, clicks, and leads generated, create a compelling impact report that quantifies the value delivered. Calculate ROI, cost-per-lead, engagement rates, and year-over-year improvements where possible. Also suggest the ideal renewal package tier based on performance. Respond JSON: {sponsor_name: string, sponsor_email: string, current_package: string, current_amount: string, expiry_date: string, impressions: number, clicks: number, leads: number, roi_estimate: string, cost_per_lead: string, engagement_rate: string, impact_summary: string, recommended_package: string, recommended_amount: string, price_justification: string, account_manager: string}",
        "Sponsorship data: {sponsorship_data_raw}. Current date: " +
          new Date().toISOString().slice(0, 10) +
          ". Scan for sponsorships expiring within 60 days.",
        [
          { field: "sponsor_name", as: "sponsor_name", type: "string" },
          { field: "sponsor_email", as: "sponsor_email", type: "string" },
          { field: "current_package", as: "current_package", type: "string" },
          { field: "current_amount", as: "current_amount", type: "string" },
          { field: "expiry_date", as: "expiry_date", type: "string" },
          { field: "impressions", as: "impressions", type: "number" },
          { field: "clicks", as: "clicks", type: "number" },
          { field: "leads", as: "leads_generated", type: "number" },
          { field: "roi_estimate", as: "roi_estimate", type: "string" },
          { field: "cost_per_lead", as: "cost_per_lead", type: "string" },
          { field: "engagement_rate", as: "engagement_rate", type: "string" },
          { field: "impact_summary", as: "impact_summary", type: "string" },
          {
            field: "recommended_package",
            as: "recommended_package",
            type: "string",
          },
          {
            field: "recommended_amount",
            as: "recommended_amount",
            type: "string",
          },
          {
            field: "price_justification",
            as: "price_justification",
            type: "string",
          },
          {
            field: "account_manager",
            as: "account_manager_email",
            type: "string",
          },
        ],
      ),

      // 4. Package recommendation (already part of AI output, condition for pricing change)
      n("4", "condition", "Recommending package upgrade?", 760, {
        conditionData: [
          {
            variable: "recommended_package",
            value: "{current_package}",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Renewal proposal email
      emailNode(
        "5",
        "Send renewal proposal email",
        1000,
        "{sponsor_email}",
        "Your Sponsorship Impact Report & Renewal Proposal — {sponsor_name}",
        "Dear {sponsor_name},\\n\\nAs your {current_package} sponsorship approaches renewal on {expiry_date}, we wanted to share the incredible impact of your partnership.\\n\\n--- Impact Report ---\\n{impact_summary}\\n\\nKey Metrics:\\n- Impressions: {impressions}\\n- Clicks: {clicks}\\n- Leads Generated: {leads_generated}\\n- Engagement Rate: {engagement_rate}\\n- ROI Estimate: {roi_estimate}\\n- Cost Per Lead: {cost_per_lead}\\n\\n--- Renewal Recommendation ---\\nPackage: {recommended_package}\\nInvestment: ${recommended_amount}\\n{price_justification}\\n\\nWe would love to continue this partnership and explore how we can deliver even greater value in the coming year.\\n\\nPlease let us know your interest by replying to this email or scheduling a call with your account manager.",
      ),

      // 6. Follow-up condition (delay then check)
      n("6", "delayAction", "Wait 7 days for response", 1240, {
        delayDetails: { delayType: "fixed", delayValue: 7, delayUnit: "days" },
      }),

      // 7. Scarcity follow-up email
      emailNode(
        "7",
        "Send scarcity follow-up email",
        1480,
        "{sponsor_email}",
        "Limited Availability: {recommended_package} Sponsorship — {sponsor_name}",
        "Dear {sponsor_name},\\n\\nWe wanted to follow up on our renewal proposal sent last week. We have been receiving strong interest in our sponsorship packages for the upcoming season.\\n\\nYour {current_package} slot is being held for you until {expiry_date}, but we have limited {recommended_package} positions available.\\n\\nAs a reminder of your results:\\n{impact_summary}\\n\\nTo secure your renewal at the proposed ${recommended_amount}, please respond by the end of this week.\\n\\nWe truly value your partnership and hope to continue working together.",
      ),

      // 8. Sponsorship owner Slack alert
      slackNode(
        "8",
        "Slack: Sponsorship renewal status",
        1720,
        ":handshake: *Sponsorship Renewal Alert*\\nSponsor: {sponsor_name} ({sponsor_email})\\nCurrent: {current_package} at ${current_amount}\\nExpiry: {expiry_date}\\nRecommended: {recommended_package} at ${recommended_amount}\\n\\nImpact: {impressions} impressions, {clicks} clicks, {leads_generated} leads\\nROI: {roi_estimate} | CPL: {cost_per_lead}\\n\\nRenewal proposal + scarcity follow-up sent. Monitor for response.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // upgrade recommended -> send with upgrade pitch
      e("4", "5", "output_2"), // same package -> send standard renewal
      e("5", "6"), // proposal sent -> wait
      e("6", "7"), // after wait -> scarcity follow-up
      e("7", "8"), // follow-up -> Slack alert
    ],
  },
};
